import { useState, useEffect, useRef, useCallback, useMemo, DragEvent } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { DocList, DocumentMetadataDrawer, DriveBreadcrumb } from './components';
import GoogleSheetWorkspacePanel from './components/GoogleSheetWorkspacePanel';
import type { DriveLocation } from './components';
import FileUpload, { type UploadCompletionPayload } from './components/FileUpload'
import DocEdit from './components/DocEdit'
import { ChevronLeft, ChevronRight, FileText, AlertCircle, X, Search, Folder, FolderPlus, CheckCircle2, Loader2 } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query';
import documentsApi, { type UserGlobalFilesQuery } from "../../api/documentsApi"
import userMetadataApi from "../../api/userMetadataApi"
import type { GlobalFile, DocInvoice, UpdateInvoiceRequest, WorkspaceTreeCategoryNode, WorkspaceTreeFolderNode } from "../../api/types"
import { useIsMobile } from '../../utils/hooks'
import ChatPanel from '../../features/chat/ChatPanel'
import ChatButton from '../../components/ChatButton'
import SearchableBusinessSelect from '../../components/SearchableBusinessSelect'
import InvoiceConfirmationModal from './components/InvoiceConfirmationModal'
import BatchPriceIncreaseModal, { DocumentPriceIncreases } from './components/BatchPriceIncreaseModal'
import { useWorkspace } from '../../context/WorkspaceContext'
import { getDocumentVendorName } from '../../utils/getDocumentVendorName'
import { getUserFriendlyErrorMessage } from '../../utils/errorMessages'
import { toast } from 'react-hot-toast'
import type { WorkspaceDragPayload } from './utils/workspaceDrag'
import { isTutorialRuntimeActive } from '../../components/tutorial/tutorialRuntime'
import {
    buildWorkspaceFolderPath,
    categoryLabelForKey,
    categoryRootFolderPath,
    defaultFolderPathForCategory,
    displayWorkspaceFolderPathForFile,
    googleSheetsWorkspaceFolderPath,
} from './utils/workspacePaths'
import { formatUploadDate } from '../../utils/formatDate'
import { findFolderMatchByPath, flattenFolderNodes } from './utils/workspaceTree'
import { addDays, formatDateKey } from '../../utils/tutorialDates'

type DriveFolderRow = {
    key: string;
    name: string;
    categoryKey?: string;
    itemCountLabel: string;
    lastModifiedLabel?: string;
    folderPath?: string;
    pathLabel?: string;
    onOpen: () => void;
    dropTargetPath?: string;
    folderDragSourcePath?: string;
    renameableFolderPath?: string;
};

type OrganizeBrowserRow = {
    key: string;
    name: string;
    subtitle: string;
    childCount: number;
    onOpen: () => void;
};

type UploadNotice = {
    tone: 'success' | 'processing' | 'error';
    title: string;
    detail?: string;
};

type ActiveUploadJob = {
    jobId: string;
    originalFilename: string;
    folderPath?: string;
    status: 'uploaded' | 'parsing' | 'extracting' | 'ready' | 'failed';
    message?: string;
    documentCount?: number | null;
    processedCount?: number | null;
    error?: string | null;
};

const getUploadJobStorageKey = (businessId?: string) =>
    `documents:active-upload-job:${businessId || 'default'}`;

const splitFilenameForRename = (filename?: string | null): { stem: string; extension: string } => {
    const value = (filename || '').trim();
    if (!value) return { stem: '', extension: '' };
    const lastDot = value.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === value.length - 1) {
        return { stem: value, extension: '' };
    }
    return {
        stem: value.slice(0, lastDot),
        extension: value.slice(lastDot),
    };
};

const categoryDocumentTypeLabel = (category?: string | null): string | null => {
    switch ((category || '').trim().toLowerCase()) {
        case 'vendor_invoice':
        case 'vendor_invoices':
        case 'invoice':
        case 'invoices':
            return 'Invoice';
        case 'vendor_bill':
        case 'vendor_bills':
        case 'bill':
        case 'bills':
            return 'Bill';
        case 'expense':
        case 'expenses':
            return 'Expense';
        case 'bank_statement':
            return 'Statement';
        case 'pl_report':
            return 'P&L report';
        case 'balance_sheet':
            return 'Balance sheet';
        case 'payroll':
            return 'Payroll';
        case 'tax':
            return 'Tax document';
        case 'loan':
            return 'Loan document';
        case 'contract':
            return 'Contract';
        default:
            return null;
    }
};

const FIXED_WORKSPACE_CATEGORY_KEYS = [
    'invoice',
    'bill',
    'expense',
    'other',
] as const;

const MANUAL_EMPTY_FOLDERS_ENABLED = true;

const normalizeDocumentType = (value?: string | null): 'invoice' | 'bills' | 'expenses' | 'other' => {
    const key = (value || '').trim().toLowerCase();
    if (key === 'invoice' || key === 'invoices' || key === 'vendor_invoice' || key === 'vendor_invoices') {
        return 'invoice';
    }
    if (key === 'bill' || key === 'bills' || key === 'vendor_bill' || key === 'vendor_bills') {
        return 'bills';
    }
    if (key === 'expense' || key === 'expenses') {
        return 'expenses';
    }
    return 'other';
};

type DocumentsTutorialStage = 'upload' | 'type' | 'review' | null;

const DocumentsTutorialMock = ({ stage, onClose }: { stage: DocumentsTutorialStage; onClose: () => void }) => {
    if (!stage) return null;

    if (stage === 'review') {
        const now = new Date().toISOString();
        const transactionDate = formatDateKey(addDays(new Date(), -14));
        const dueDate = formatDateKey(addDays(new Date(), 16));
        const sampleLineItems = [
            {
                raw_description: 'BUTTER SOLID AA UNSALTED',
                description: 'Butter Solid AA Unsalted',
                canonical_name: 'Butter Solid AA Unsalted',
                brand_name: 'Grassland',
                amount: 98.28,
                line_amount: 98.28,
                quantity: 36,
                purchase_quantity: 36,
                unit: 'lb',
                purchase_unit: 'lb',
                unit_price: 2.73,
                purchase_unit_price: 2.73,
            },
            {
                raw_description: 'HEAVY CREAM 40% UHT',
                description: 'Heavy Cream 40% UHT',
                canonical_name: 'Heavy Cream 40% UHT',
                brand_name: 'Farmland',
                amount: 53.4,
                line_amount: 53.4,
                quantity: 12,
                purchase_quantity: 12,
                unit: '32 oz',
                purchase_unit: '32 oz',
                unit_price: 4.45,
                purchase_unit_price: 4.45,
            },
            {
                raw_description: 'CHEESE BURRATA 2-4OZ',
                description: 'Cheese Burrata 2-4oz',
                canonical_name: 'Cheese Burrata 2-4oz',
                brand_name: 'BelGioso',
                amount: 20.75,
                line_amount: 20.75,
                quantity: 3,
                purchase_quantity: 3,
                unit: 'lb',
                purchase_unit: 'lb',
                unit_price: 20.75,
                purchase_unit_price: 20.75,
            },
            {
                raw_description: 'CHEESE MOZZARELLA SHREDDED WM',
                description: 'Cheese Mozzarella Shredded WM',
                canonical_name: 'Cheese Mozzarella Shredded WM',
                brand_name: 'Galbani',
                amount: 80.56,
                line_amount: 80.56,
                quantity: 30,
                purchase_quantity: 30,
                unit: 'lb',
                purchase_unit: 'lb',
                unit_price: 80.56,
                purchase_unit_price: 80.56,
            },
            {
                raw_description: 'EGGS X-LARGE LOOSE AA',
                description: 'Eggs X-Large Loose AA',
                canonical_name: 'Eggs X-Large Loose AA',
                brand_name: 'Packer',
                amount: 54,
                line_amount: 54,
                quantity: 30,
                purchase_quantity: 30,
                unit: 'doz',
                purchase_unit: 'doz',
                unit_price: 1.8,
                purchase_unit_price: 1.8,
            },
            {
                raw_description: 'BACON 13/17 CNTRY',
                description: 'Bacon 13/17 Country',
                canonical_name: 'Bacon 13/17 Country',
                brand_name: 'Mariah',
                amount: 166.8,
                line_amount: 166.8,
                quantity: 30,
                purchase_quantity: 30,
                unit: 'lb',
                purchase_unit: 'lb',
                unit_price: 5.56,
                purchase_unit_price: 5.56,
            },
            {
                raw_description: 'BF HANGING TENDER 1855 ANGUS',
                description: 'BF Hanging Tender 1855 Angus',
                canonical_name: 'BF Hanging Tender 1855 Angus',
                brand_name: 'Swift 1855',
                amount: 305.42,
                line_amount: 305.42,
                quantity: 43.2,
                purchase_quantity: 43.2,
                unit: 'lb',
                purchase_unit: 'lb',
                unit_price: 7.07,
                purchase_unit_price: 7.07,
            },
            {
                raw_description: 'BEEF BURGER SLIDER 2.75 75/25',
                description: 'Beef Burger Slider 2.75 75/25',
                canonical_name: 'Beef Burger Slider 2.75 75/25',
                brand_name: 'Wonder',
                amount: 181.5,
                line_amount: 181.5,
                quantity: 33,
                purchase_quantity: 33,
                unit: 'lb',
                purchase_unit: 'lb',
                unit_price: 5.5,
                purchase_unit_price: 5.5,
            },
            {
                raw_description: 'BF BURGER 80/20 PERFECT BLEND',
                description: 'BF Burger 80/20 Perfect Blend',
                canonical_name: 'BF Burger 80/20 Perfect Blend',
                brand_name: 'Cutshop',
                amount: 309,
                line_amount: 309,
                quantity: 60,
                purchase_quantity: 60,
                unit: 'lb',
                purchase_unit: 'lb',
                unit_price: 5.15,
                purchase_unit_price: 5.15,
            },
            {
                raw_description: 'CHICKEN PARTY WING HALAL',
                description: 'Chicken Party Wing Halal',
                canonical_name: 'Chicken Party Wing Halal',
                brand_name: 'Mountaire',
                amount: 71.2,
                line_amount: 71.2,
                quantity: 40,
                purchase_quantity: 40,
                unit: 'lb',
                purchase_unit: 'lb',
                unit_price: 1.78,
                purchase_unit_price: 1.78,
            },
            {
                raw_description: 'PORK BUTT BONELESS FRESH',
                description: 'Pork Butt Boneless Fresh',
                canonical_name: 'Pork Butt Boneless Fresh',
                brand_name: 'Supplier A',
                amount: 304,
                line_amount: 304,
                quantity: 32,
                purchase_quantity: 32,
                unit: 'lb',
                purchase_unit: 'lb',
                unit_price: 9.5,
                purchase_unit_price: 9.5,
            },
            {
                raw_description: 'ASPARAGUS STANDARD',
                description: 'Asparagus Standard',
                canonical_name: 'Asparagus Standard',
                brand_name: 'Fresh',
                amount: 82.4,
                line_amount: 82.4,
                quantity: 22,
                purchase_quantity: 22,
                unit: 'lb',
                purchase_unit: 'lb',
                unit_price: 41.2,
                purchase_unit_price: 41.2,
            },
            {
                raw_description: 'BROCCOLINI',
                description: 'Broccolini',
                canonical_name: 'Broccolini',
                brand_name: 'Fresh',
                amount: 299.82,
                line_amount: 299.82,
                quantity: 36,
                purchase_quantity: 36,
                unit: 'ct',
                purchase_unit: 'ct',
                unit_price: 99.94,
                purchase_unit_price: 99.94,
            },
        ];
        const sampleLineItemsTotal = Number(sampleLineItems.reduce((sum, item) => sum + (item.line_amount || item.amount || 0), 0).toFixed(2));
        const sampleTaxTotal = 25.19;
        const sampleInvoiceTotal = Number((sampleLineItemsTotal + sampleTaxTotal).toFixed(2));
        const fakeInvoice = {
            _id: '000000000000000000060576',
            id: '000000000000000000060576',
            document_type: 'bill',
            user_id: 'tutorial-user',
            file_id: 'tutorial-bill-file',
            preview_page: 1,
            page_count: 1,
            data: {
                transaction_date: transactionDate,
                due_date: dueDate,
                invoice_number: '072191',
                total_amount: sampleInvoiceTotal,
                balance_due: sampleInvoiceTotal,
                currency: 'USD',
                vendor: { name: 'Supplier A' },
                line_items: sampleLineItems,
                tax_details: { total_tax: sampleTaxTotal },
                metadata: {
                    create_time: now,
                    last_updated_time: now,
                },
                status: 'pending',
                extraction_method: 'tutorial_sample',
            },
            original_parsed_data: {
                vendor: { name: 'Supplier A' },
                invoice_number: '072191',
                transaction_date: transactionDate,
                due_date: dueDate,
                total_amount: sampleInvoiceTotal,
                currency: 'USD',
                line_items: sampleLineItems,
                metadata: {
                    create_time: now,
                    last_updated_time: now,
                },
                status: 'pending',
            },
        } as DocInvoice;

        return (
            <InvoiceConfirmationModal
                documentType="bills"
                documentId="000000000000000000060576"
                preFetchedInvoice={fakeInvoice}
                sourceFilename="INVOICE-072191-scan.pdf"
                sourcePreviewPage={1}
                tutorialMode
                tutorialPreviewUrl="/tutorial-assets/invoice-072191-preview.png"
                tutorialPreviewTarget="documents-tutorial-preview"
                onClose={onClose}
                onConfirm={onClose}
            />
        );
    }

    const isTypeStep = stage === 'type';

    return (
        <div className="fixed inset-0 z-[2100] flex items-center justify-center bg-black/30 px-8 py-6">
            <div className="w-[min(620px,calc(100vw-96px))] rounded-xl bg-white p-6 shadow-2xl">
                <div className="mb-5 flex items-center justify-between">
                    <h2 className="text-base font-semibold text-[#1F2937]">Upload documents</h2>
                    <span className="h-6 w-6" aria-hidden />
                </div>
                <div data-tutorial="documents-tutorial-upload-file" className="mb-5 flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50 text-red-600">
                        <FileText className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-[#1F2937]">Bill.pdf</div>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-[#697079]">
                            <span>609KB of 120KB</span>
                            <span className="inline-flex items-center gap-1 text-green-600">
                                <CheckCircle2 className="h-3 w-3" /> Uploaded
                            </span>
                        </div>
                    </div>
                </div>
                <div className="mb-5">
                    <label className="mb-2 block text-sm font-medium text-[#1F2937]">Document type<span className="text-red-500">*</span></label>
                    <div className="relative">
                        <button
                            data-tutorial="documents-tutorial-type-select"
                            className={`flex h-10 w-full items-center justify-between rounded-lg border px-3 text-left text-sm ${isTypeStep ? 'border-[#5B5DE4]' : 'border-gray-200'}`}
                        >
                            <span className={isTypeStep ? 'text-[#1F2937]' : 'text-[#9CA3AF]'}>
                                {isTypeStep ? 'Bill' : 'Select document type'}
                            </span>
                            <ChevronRight className={`h-4 w-4 text-[#697079] ${isTypeStep ? '-rotate-90' : 'rotate-90'}`} />
                        </button>
                        {isTypeStep && (
                            <div className="absolute left-0 right-0 top-[44px] z-10 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
                                {[
                                    ['Invoice', 'Vendor invoice with item-level details (affects inventory)'],
                                    ['Expenses', 'Receipts or small purchases (no inventory tracking)'],
                                    ['Bills', 'Unpaid invoice (no item breakdown)'],
                                    ['Other', ''],
                                ].map(([label, help]) => (
                                    <div key={label} className={`px-4 py-2.5 text-sm ${label === 'Bills' ? 'bg-blue-50 text-[#1F2937]' : 'text-[#4C545F]'}`}>
                                        <span>{label}</span>
                                        {help && <span className="ml-1 text-xs italic text-[#989DA3]">{help}</span>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="mb-6">
                    <label className="mb-2 block text-sm font-medium text-[#1F2937]">Tags</label>
                    <input readOnly placeholder="Type to add a tag" className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm placeholder:text-[#9CA3AF]" />
                </div>
                <div className="flex justify-end gap-3">
                    <button type="button" className="h-9 rounded-lg border border-gray-300 px-5 text-sm text-[#4C545F]">Cancel</button>
                    <button type="button" className="h-9 rounded-lg bg-[#1F2937] px-6 text-sm font-medium text-white">Upload</button>
                </div>
            </div>
        </div>
    );
};

export default function DocPage() {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const isAdminEmbedded = location.pathname.startsWith('/admin/business/');
    const isMobile = useIsMobile()
    const { workspaces, activeBusinessId, setActiveBusinessId } = useWorkspace();
    const [showChat, setShowChat] = useState(false)
    const [extendChat, setExtendChat] = useState(false)
    const [showSetupGuidePanel, setShowSetupGuidePanel] = useState(false)

    const [showEdit, setShowEdit] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [deleteRelatedData, setDeleteRelatedData] = useState(false);
    const [selectedFile, setSelectedFile] = useState<GlobalFile | null>(null);
    const [deleteRelatedRecords, setDeleteRelatedRecords] = useState(false);

    const [isEmpty, setIsEmpty] = useState(false);

    const [files, setFiles] = useState<GlobalFile[]>([]);
    const [allInvoiceDocs, setAllInvoiceDocs] = useState<DocInvoice[]>([]);
    const [allBillDocs, setAllBillDocs] = useState<DocInvoice[]>([]);
    const [pendingSourceFiles, setPendingSourceFiles] = useState<GlobalFile[]>([]);
    const [selectedInvoice, setSelectedInvoice] = useState<DocInvoice | null>(null);
    const [selectedBill, setSelectedBill] = useState<DocInvoice | null>(null);
    const [selectedInvoicePreview, setSelectedInvoicePreview] = useState<{ filename?: string; previewPages?: number[]; previewPage?: number | null; forceSingle?: boolean }>({});
    const [selectedBillPreview, setSelectedBillPreview] = useState<{ filename?: string; previewPages?: number[]; previewPage?: number | null; forceSingle?: boolean }>({});
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [showBillModal, setShowBillModal] = useState(false);
    const [showQboSyncModal, setShowQboSyncModal] = useState(false);
    const [selectedQboSyncFile, setSelectedQboSyncFile] = useState<GlobalFile | null>(null);
    const [selectedQboSyncDoc, setSelectedQboSyncDoc] = useState<DocInvoice | null>(null);
    const [selectedQboSyncType, setSelectedQboSyncType] = useState<'invoice' | 'bills' | null>(null);
    const [qboSyncLoading, setQboSyncLoading] = useState(false);
    const [qboSyncError, setQboSyncError] = useState<string | null>(null);
    const [qboSyncOverrides, setQboSyncOverrides] = useState<Record<string, Partial<GlobalFile>>>({});
    const [showBatchPriceIncreaseModal, setShowBatchPriceIncreaseModal] = useState(false);
    const [batchPriceIncreaseDocs, setBatchPriceIncreaseDocs] = useState<DocumentPriceIncreases[]>([]);
    const [handledDeepLinkTarget, setHandledDeepLinkTarget] = useState<string>('');
    const [pageDragActive, setPageDragActive] = useState(false);
    const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
    const dragDepthRef = useRef(0);

    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [pageSize] = useState(5);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [driveLocation, setDriveLocation] = useState<DriveLocation>({ kind: 'root' });
    const [metadataFile, setMetadataFile] = useState<GlobalFile | null>(null);
    /** Full main-area embedded Google Sheets editor (see GoogleSheetWorkspacePanel). */
    const [sheetWorkspaceFile, setSheetWorkspaceFile] = useState<GlobalFile | null>(null);
    const fileRequestSeqRef = useRef(0);
    const autoReclassifyAttemptedRef = useRef<Set<string>>(new Set());
    const onboardingStep3CompletedRef = useRef<Set<string>>(new Set());
    const uploadJobHydratedRef = useRef(false);
    const [documentSearchInput, setDocumentSearchInput] = useState('');
    const [documentSearchQuery, setDocumentSearchQuery] = useState('');
    const [newFolderCategoryKey, setNewFolderCategoryKey] = useState('');
    const [newFolderOpen, setNewFolderOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [newFolderParentPathOverride, setNewFolderParentPathOverride] = useState<string | null>(null);
    const [organizeOpen, setOrganizeOpen] = useState(false);
    const [organizeTargetPath, setOrganizeTargetPath] = useState('');
    const [organizeSelection, setOrganizeSelection] = useState<GlobalFile[]>([]);
    const [organizeFolderSource, setOrganizeFolderSource] = useState<{ folderPath: string; name: string } | null>(null);
    const [organizeNameDraft, setOrganizeNameDraft] = useState('');
    const [organizeLocation, setOrganizeLocation] = useState<DriveLocation>({ kind: 'root' });
    const [organizeSearch, setOrganizeSearch] = useState('');
    const [uploadNotice, setUploadNotice] = useState<UploadNotice | null>(null);
    const [activeUploadJob, setActiveUploadJob] = useState<ActiveUploadJob | null>(null);
    const [documentsTutorialStage, setDocumentsTutorialStage] = useState<DocumentsTutorialStage>(null);
    const [isTutorialDemo, setIsTutorialDemo] = useState(() => isTutorialRuntimeActive('documents'));

    // Admin Business Selector Logic
    const { data: userMetadata } = useQuery({
        queryKey: ['userMetadata'],
        queryFn: () => userMetadataApi.getUserMetadata(),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });
    const isAdmin = userMetadata?.role === 'admin';

    const [selectedBusinessId, setSelectedBusinessId] = useState<string>('');

    const searchParamsString = searchParams.toString();
    const targetFileId = (searchParams.get('target_file_id') || '').trim();
    const targetDocTypeValue = normalizeDocumentType(searchParams.get('target_doc_type'));
    const targetDocType: 'invoice' | 'expenses' | 'bills' | null =
        targetDocTypeValue === 'other' ? null : targetDocTypeValue;

    useEffect(() => {
        const handleTutorialStep = (event: Event) => {
            const detail = (event as CustomEvent).detail as { pageKey?: string } | undefined;
            if (detail?.pageKey === 'documents') {
                setIsTutorialDemo(true);
            }
        };
        const handleTutorialActive = (event: Event) => {
            const detail = (event as CustomEvent<{ pageKey?: string; active?: boolean }>).detail;
            setIsTutorialDemo(detail?.active !== false && detail?.pageKey === 'documents');
        };
        const openUpload = () => setDocumentsTutorialStage('upload');
        const openTypeDropdown = () => setDocumentsTutorialStage('type');
        const openReview = () => setDocumentsTutorialStage('review');
        const handleTutorialClosed = (event: Event) => {
            const detail = (event as CustomEvent<{ pageKey?: string }>).detail;
            if (detail?.pageKey === 'documents') {
                setDocumentsTutorialStage(null);
                setIsTutorialDemo(false);
            }
        };

        if (isTutorialRuntimeActive('documents')) setIsTutorialDemo(true);
        window.addEventListener('finz:tutorial:active', handleTutorialActive);
        window.addEventListener('finz:tutorial:step', handleTutorialStep);
        window.addEventListener('finz:tutorial:documents-open-upload', openUpload);
        window.addEventListener('finz:tutorial:documents-open-type-dropdown', openTypeDropdown);
        window.addEventListener('finz:tutorial:documents-open-review', openReview);
        window.addEventListener('finz:tutorial:closed', handleTutorialClosed);

        return () => {
            window.removeEventListener('finz:tutorial:active', handleTutorialActive);
            window.removeEventListener('finz:tutorial:step', handleTutorialStep);
            window.removeEventListener('finz:tutorial:documents-open-upload', openUpload);
            window.removeEventListener('finz:tutorial:documents-open-type-dropdown', openTypeDropdown);
            window.removeEventListener('finz:tutorial:documents-open-review', openReview);
            window.removeEventListener('finz:tutorial:closed', handleTutorialClosed);
        };
    }, []);

    useEffect(() => {
        const handleSetupGuideVisibility = (event: Event) => {
            setShowSetupGuidePanel(Boolean((event as CustomEvent<{ open?: boolean }>).detail?.open));
        };

        setShowSetupGuidePanel(document.body.classList.contains('finz-setup-guide-open'));
        window.addEventListener('finz:setup-guide:visibility', handleSetupGuideVisibility);
        return () => window.removeEventListener('finz:setup-guide:visibility', handleSetupGuideVisibility);
    }, []);

    const effectiveBusinessId = activeBusinessId || undefined;
    const isInvoiceLikeType = (fileType?: string | null) => normalizeDocumentType(fileType) !== 'other';

    useEffect(() => {
        setPage(1);
    }, [driveLocation]);

    useEffect(() => {
        if (documentSearchQuery) {
            return;
        }
        if (driveLocation.kind === 'root' || driveLocation.kind === 'category') {
            setFiles([]);
            setIsEmpty(false);
        }
    }, [driveLocation, documentSearchQuery]);

    useEffect(() => {
        setDriveLocation({ kind: 'root' });
        setPage(1);
    }, [effectiveBusinessId]);

    useEffect(() => {
        if (!effectiveBusinessId || typeof window === 'undefined') return;
        const raw = window.sessionStorage.getItem(getUploadJobStorageKey(effectiveBusinessId));
        if (!raw) {
            uploadJobHydratedRef.current = true;
            return;
        }
        try {
            const parsed = JSON.parse(raw) as { activeUploadJob?: ActiveUploadJob | null; uploadNotice?: UploadNotice | null };
            if (parsed.activeUploadJob?.jobId) {
                setActiveUploadJob(parsed.activeUploadJob);
            }
            if (parsed.uploadNotice?.tone === 'processing') {
                setUploadNotice(parsed.uploadNotice);
            }
        } catch {
            window.sessionStorage.removeItem(getUploadJobStorageKey(effectiveBusinessId));
        } finally {
            uploadJobHydratedRef.current = true;
        }
    }, [effectiveBusinessId]);

    useEffect(() => {
        if (!effectiveBusinessId || typeof window === 'undefined') return;
        if (!uploadJobHydratedRef.current) return;
        const storageKey = getUploadJobStorageKey(effectiveBusinessId);
        if (!activeUploadJob?.jobId) {
            window.sessionStorage.removeItem(storageKey);
            return;
        }
        window.sessionStorage.setItem(
            storageKey,
            JSON.stringify({
                activeUploadJob,
                uploadNotice: uploadNotice?.tone === 'processing' ? uploadNotice : null,
            }),
        );
    }, [activeUploadJob, effectiveBusinessId, uploadNotice]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDocumentSearchQuery(documentSearchInput.trim());
        }, 180);
        return () => window.clearTimeout(timeoutId);
    }, [documentSearchInput]);

    useEffect(() => {
        setPage(1);
    }, [documentSearchQuery]);

    const handleDriveNavigate = (next: DriveLocation) => {
        setDriveLocation(next);
    };

    const { data: workspaceTreeData } = useQuery({
        queryKey: ['workspace-tree', effectiveBusinessId],
        queryFn: () => documentsApi.getWorkspaceTree(effectiveBusinessId),
        enabled: Boolean(effectiveBusinessId),
        refetchOnWindowFocus: false,
    });
    const workspaceTree = useMemo<WorkspaceTreeCategoryNode[]>(() => {
        const normalizeCategoryKey = (value?: string | null) => {
            let key = (value || '').trim().toLowerCase();
            if (key === 'invoice' || key === 'invoices' || key === 'vendor_invoices' || key === 'vendor_invoice') {
                key = 'invoice';
            } else if (key === 'bill' || key === 'bills' || key === 'vendor_bills' || key === 'vendor_bill') {
                key = 'bill';
            } else if (key === 'expenses') {
                key = 'expense';
            }
            return key === 'unknown' || key === '' ? 'other' : key;
        };
        const normalizeLabel = (value?: string | null, fallback = 'Unknown') => {
            const label = (value || '').trim();
            return label || fallback;
        };
        const normalizeFolder = (node?: WorkspaceTreeFolderNode | null): WorkspaceTreeFolderNode | null => {
            if (!node) return null;
            const folderPath = (node.folder_path || '').trim();
            if (!folderPath) return null;
            return {
                name: normalizeLabel(node.name, 'Unknown'),
                folder_path: folderPath,
                count: node.count ?? 0,
                direct_count: node.direct_count ?? node.count ?? 0,
                children: (node.children || [])
                    .map((child) => normalizeFolder(child))
                    .filter((child): child is WorkspaceTreeFolderNode => Boolean(child)),
            };
        };

        const dedupedMap = new Map<string, WorkspaceTreeCategoryNode>();
        for (const cat of (workspaceTreeData?.tree ?? [])) {
            const categoryKey = normalizeCategoryKey(cat.category);
            const folders = (cat.folders || [])
                .map((folder) => normalizeFolder(folder))
                .filter((folder): folder is WorkspaceTreeFolderNode => Boolean(folder));

            const existing = dedupedMap.get(categoryKey);
            if (existing) {
                // Merge folders and entities if they exist
                existing.count += cat.count ?? 0;
                existing.folders = [...(existing.folders || []), ...folders];
                // Note: entities and labels could be merged too if needed
            } else {
                dedupedMap.set(categoryKey, {
                    category: categoryKey,
                    label: normalizeLabel(cat.label, 'Other'),
                    count: cat.count ?? 0,
                    entities: cat.entities ?? [],
                    folders,
                });
            }
        }

        return Array.from(dedupedMap.values());
    }, [workspaceTreeData?.tree]);

    /** Smart workspace sidebar / summary read from React Query — refetch so folder mutations show immediately. */
    const refreshWorkspaceFolders = useCallback(async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['workspace-tree', effectiveBusinessId] }),
            queryClient.invalidateQueries({ queryKey: ['workspace-summary', effectiveBusinessId] }),
            queryClient.refetchQueries({ queryKey: ['workspace-tree', effectiveBusinessId], type: 'active' }),
            queryClient.refetchQueries({ queryKey: ['workspace-summary', effectiveBusinessId], type: 'active' }),
        ]);
    }, [queryClient, effectiveBusinessId]);

    const { data: tagLabelsData, refetch: refetchTagLabels } = useQuery({
        queryKey: ['document-tag-labels', effectiveBusinessId],
        queryFn: () => documentsApi.getTagLabels(effectiveBusinessId),
        enabled: !!effectiveBusinessId,
        refetchOnWindowFocus: false,
    });
    const availableTagLabels = tagLabelsData?.labels || [];

    const openFolderLocation = useCallback((folderPath: string) => {
        const match = findFolderMatchByPath(workspaceTree, folderPath);
        if (!match) return;
        handleDriveNavigate({
            kind: 'folder',
            categoryKey: match.categoryNode.category,
            categoryLabel: match.categoryNode.label,
            folderName: match.folderNode.name,
            folderPath: match.folderNode.folder_path,
            ancestors: match.ancestors,
        });
    }, [workspaceTree]);

    const formatWorkspacePathLabel = useCallback((path?: string | null) => {
        const normalized = (path || '').trim().replace(/\/+$/, '');
        if (!normalized) return 'Documents';
        return normalized.split('/').filter(Boolean).join(' / ');
    }, []);

    const buildDriveLocationFromPath = useCallback((path?: string | null, categoryKeyHint?: string | null): DriveLocation | null => {
        const normalized = (path || '').trim().replace(/\/+$/, '');
        if (!normalized) return null;
        const segments = normalized.split('/').filter(Boolean);
        if (segments[0] !== 'Documents') return null;

        const categoryLabel = segments[1];
        const categoryKey = (categoryKeyHint || workspaceTree.find((node) => node.label === categoryLabel)?.category || '').trim();
        if (!categoryLabel || !categoryKey) {
            return { kind: 'root' };
        }

        if (segments.length <= 2) {
            return {
                kind: 'category',
                categoryKey,
                categoryLabel,
            };
        }

        const relativeSegments = segments.slice(2);
        const ancestors = relativeSegments.slice(0, -1).map((name, index) => ({
            name,
            folderPath: `Documents/${categoryLabel}/${relativeSegments.slice(0, index + 1).join('/')}`,
        }));

        return {
            kind: 'folder',
            categoryKey,
            categoryLabel,
            folderName: relativeSegments[relativeSegments.length - 1],
            folderPath: normalized,
            ancestors,
        };
    }, [workspaceTree]);

    const revealLatestFileLocation = useCallback(async (fileId?: string | null, delayMs: number = 0) => {
        if (!fileId || !effectiveBusinessId) return false;
        if (delayMs > 0) {
            await new Promise((resolve) => window.setTimeout(resolve, delayMs));
        }
        try {
            const latestFile = await documentsApi.getUserGlobalFile(fileId, effectiveBusinessId);
            const location = buildDriveLocationFromPath(
                latestFile.workspace_folder_path,
                latestFile.workspace_category,
            );
            if (!location) return false;
            setDriveLocation(location);
            setPage(1);
            return latestFile;
        } catch (error) {
            console.error('Failed to reveal latest file location', error);
            return false;
        }
    }, [buildDriveLocationFromPath, effectiveBusinessId]);

    const hasDraggedFiles = (event: DragEvent<HTMLElement>) =>
        Array.from(event.dataTransfer?.types || []).includes('Files');

    const getWorkspaceCategoryKeyForPath = useCallback((path?: string | null) => {
        const normalizedPath = (path || '').trim().replace(/\/+$/, '');
        if (!normalizedPath) return '';
        const matchedCategory = workspaceTree.find((categoryNode) => {
            const rootPath = categoryRootFolderPath(categoryNode.category);
            return normalizedPath === rootPath || normalizedPath.startsWith(`${rootPath}/`);
        });
        return matchedCategory?.category || '';
    }, [workspaceTree]);

    const handlePageDragEnter = (event: DragEvent<HTMLDivElement>) => {
        if (!hasDraggedFiles(event)) return;
        event.preventDefault();
        dragDepthRef.current += 1;
        setPageDragActive(true);
    };

    const handlePageDragOver = (event: DragEvent<HTMLDivElement>) => {
        if (!hasDraggedFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        if (!pageDragActive) {
            setPageDragActive(true);
        }
    };

    const handlePageDragLeave = (event: DragEvent<HTMLDivElement>) => {
        if (!hasDraggedFiles(event)) return;
        event.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setPageDragActive(false);
        }
    };

    const handlePageDrop = (event: DragEvent<HTMLDivElement>) => {
        if (!hasDraggedFiles(event)) return;
        event.preventDefault();
        dragDepthRef.current = 0;
        setPageDragActive(false);
        setDroppedFiles(Array.from(event.dataTransfer.files || []));
    };

    const userBusinessOptions = Array.from(
        new Map(
            workspaces.flatMap((workspace) =>
                (workspace.businesses || []).map((business) => [
                    business.id,
                    {
                        id: business.id,
                        name: business.legal_name || business.dba_name || 'Unnamed Business',
                    },
                ]),
            ),
        ).values(),
    );
    const { data: businessesData } = useQuery({
        queryKey: ['allBusinesses'],
        queryFn: userMetadataApi.getAllBusinesses,
        enabled: isAdmin,
        refetchOnWindowFocus: false,
    });
    const adminBusinessOptions = (businessesData?.businesses || []).map((b: any) => ({
        id: b.business_id || b.user_id,
        name: b.legal_name || b.dba_name || b.onboarding?.qbo_metadata?.company_name || b.user_email || 'Unknown',
        subtitle: b.user_email || undefined,
    }));
    const businessOptions = isAdmin ? adminBusinessOptions : userBusinessOptions;

    const getDocId = (doc: DocInvoice | null) => doc?._id || doc?.id;
    const getPrimaryDocFileId = (doc: DocInvoice | null) =>
        doc?.file_id || doc?.source_file_id || doc?.source_file_ids?.[0] || null;
    const isPendingReviewStatus = (status?: string | null) =>
        status === 'pending' || status === 'review' || status === 'needs_review';
    const hasRelatedDocumentType = (
        doc: DocInvoice | null,
        relationshipType: 'duplicate_candidate' | 'combine_candidate',
    ) => {
        return Boolean(
            doc?.related_document_candidates?.some(
                (candidate) => candidate.relationship_type === relationshipType,
            ),
        );
    };
    const pendingInvoices = useMemo(
        () => allInvoiceDocs.filter((doc) => isPendingReviewStatus(doc.data?.status)),
        [allInvoiceDocs],
    );
    const pendingBills = useMemo(
        () => allBillDocs.filter((doc) => isPendingReviewStatus(doc.data?.status)),
        [allBillDocs],
    );
    const pendingExpenseDocs = pendingInvoices.filter((doc) => doc.document_type === 'expense');
    const pendingInvoiceDocs = pendingInvoices.filter((doc) => doc.document_type !== 'expense');
    const allInvoiceLikeDocs = [...allInvoiceDocs, ...allBillDocs];
    const resolveInvoiceLikeFile = (file: GlobalFile): GlobalFile => {
        if (!isInvoiceLikeType(file.type)) return file;
        const matchedDoc = allInvoiceLikeDocs
            .map((doc) => {
                const docId = getDocId(doc);
                const docFileId = doc.file_id || null;
                const docSourceFileId = doc.source_file_id || null;
                const docSourceFileIds = doc.source_file_ids || [];
                let score = 0;
                if (file.linked_document_id && docId && file.linked_document_id === docId) score = 100;
                else if (docFileId && docFileId === file.file_id) score = 90;
                else if (docSourceFileId && docSourceFileId === file.file_id) score = 80;
                else if (docSourceFileIds.includes(file.file_id)) score = 70;
                if (!score) return null;
                return { doc, score };
            })
            .filter((entry): entry is { doc: DocInvoice; score: number } => Boolean(entry))
            .sort((left, right) => right.score - left.score)[0]?.doc;

        const baseFile = !matchedDoc ? file : (() => {
            const matchedDocId = getDocId(matchedDoc) || file.linked_document_id || null;
            const docStatus = (matchedDoc.data?.status as string | null | undefined) || null;
            const qboSync = (matchedDoc.qbo_sync || {}) as Record<string, unknown>;
            const qboSyncStatus = typeof qboSync.sync_status === 'string' ? qboSync.sync_status : null;
            const qboEntityId = typeof qboSync.matched_entity_id === 'string' ? qboSync.matched_entity_id : null;

            return {
                ...file,
                linked_document_id: matchedDocId,
                doc_status: docStatus,
                qbo_sync_status: qboSyncStatus,
                qbo_entity_id: qboEntityId,
                qbo_synced: Boolean(
                    qboEntityId
                    || qboSyncStatus === 'posted'
                    || qboSyncStatus === 'updated'
                    || qboSyncStatus === 'already_exists'
                    || docStatus === 'posted_to_qbo'
                ),
                waiting_for_confirmation: docStatus === 'pending' || docStatus === 'review' || docStatus === 'needs_review',
            };
        })();

        const override = qboSyncOverrides[file.file_id];
        return override ? { ...baseFile, ...override } : baseFile;
    };
    const resolvedFiles = files.map(resolveInvoiceLikeFile);
    useEffect(() => {
        if (activeBusinessId) {
            if (selectedBusinessId !== activeBusinessId) {
                setSelectedBusinessId(activeBusinessId);
            }
        }
    }, [activeBusinessId, selectedBusinessId]);

    const normalizeWorkspaceCategoryForDrive = useCallback((value?: string | null) => {
        let key = (value || '').trim().toLowerCase();
        if (key === 'invoice' || key === 'invoices' || key === 'vendor_invoices' || key === 'vendor_invoice') {
            key = 'invoice';
        } else if (key === 'bill' || key === 'bills' || key === 'vendor_bills' || key === 'vendor_bill') {
            key = 'bill';
        } else if (key === 'expenses') {
            key = 'expense';
        }

        if (
            key === '' ||
            key === 'unknown' ||
            key === 'other' ||
            key === 'bank_statement' ||
            key === 'pl_report' ||
            key === 'balance_sheet' ||
            key === 'payroll' ||
            key === 'tax' ||
            key === 'loan' ||
            key === 'contract'
        ) {
            return 'other';
        }
        return key;
    }, []);

    const normalizeWorkspaceEntityForDrive = useCallback((value?: string | null) => {
        const label = (value || '').trim();
        return label || 'Unknown';
    }, []);

    const buildComparableWorkspacePath = useCallback((file: GlobalFile) => {
        return buildWorkspaceFolderPath(
            normalizeWorkspaceCategoryForDrive(file.workspace_category),
            normalizeWorkspaceEntityForDrive(file.workspace_entity),
        );
    }, [normalizeWorkspaceCategoryForDrive, normalizeWorkspaceEntityForDrive]);

    const normalizeFolderPathKey = useCallback((path: string) => path.trim().replace(/\/+$/, ''), []);

    /** Match files to the open folder, including Google Sheets whose DB path is still Other/Unknown. */
    const fileMatchesDriveFolderPath = useCallback(
        (file: GlobalFile, targetFolderPath: string) => {
            const target = normalizeFolderPathKey(targetFolderPath);
            const explicit = normalizeFolderPathKey(file.workspace_folder_path || '');
            if (explicit && explicit === target) return true;
            const displayPath = displayWorkspaceFolderPathForFile(file);
            if (displayPath && normalizeFolderPathKey(displayPath) === target) return true;
            return normalizeFolderPathKey(buildComparableWorkspacePath(file)) === target;
        },
        [buildComparableWorkspacePath, normalizeFolderPathKey],
    );

    const folderLastModifiedByPath = useMemo(() => {
        const latest = new Map<string, string>();
        for (const file of pendingSourceFiles) {
            const uploadedAt = file.upload_time || '';
            if (!uploadedAt) continue;
            const comparablePath = buildComparableWorkspacePath(file);
            const explicitPath = (file.workspace_folder_path || '').trim();
            const candidatePaths = [explicitPath, comparablePath].filter(
                (value, index, arr): value is string => Boolean(value) && arr.indexOf(value) === index,
            );
            for (const path of candidatePaths) {
                const current = latest.get(path);
                if (!current || uploadedAt > current) {
                    latest.set(path, uploadedAt);
                }
            }
        }
        return latest;
    }, [pendingSourceFiles, buildComparableWorkspacePath]);

    const categoryLastModifiedByKey = useMemo(() => {
        const latest = new Map<string, string>();
        for (const file of pendingSourceFiles) {
            const uploadedAt = file.upload_time || '';
            if (!uploadedAt) continue;
            const categoryKey = normalizeWorkspaceCategoryForDrive(file.workspace_category);
            const current = latest.get(categoryKey);
            if (!current || uploadedAt > current) {
                latest.set(categoryKey, uploadedAt);
            }
        }
        return latest;
    }, [pendingSourceFiles, normalizeWorkspaceCategoryForDrive]);

    const currentFolderMatch = useMemo(
        () => (driveLocation.kind === 'folder' ? findFolderMatchByPath(workspaceTree, driveLocation.folderPath) : null),
        [driveLocation, workspaceTree],
    );

    const documentsChatContextLabel = useMemo(() => {
        if (driveLocation.kind === 'root') return 'All folders';
        if (driveLocation.kind === 'category') return driveLocation.categoryLabel;
        if (driveLocation.kind === 'folder') return driveLocation.folderName;
        return 'All folders';
    }, [driveLocation]);

    const folderRows = useMemo<DriveFolderRow[]>(() => {
        if (driveLocation.kind === 'root') {
            return workspaceTree
                .map((cat) => ({
                    key: `category:${cat.category}`,
                    name: cat.label,
                    categoryKey: cat.category,
                    itemCountLabel: cat.count > 0 ? `${cat.count} ${cat.count === 1 ? 'item' : 'items'}` : 'Empty',
                    lastModifiedLabel: categoryLastModifiedByKey.get(cat.category)
                        ? formatUploadDate(categoryLastModifiedByKey.get(cat.category)!)
                        : '—',
                    folderPath: undefined,
                    pathLabel: undefined,
                    onOpen: () =>
                        handleDriveNavigate({
                            kind: 'category',
                            categoryKey: cat.category,
                            categoryLabel: cat.label,
                        }),
                    dropTargetPath: categoryRootFolderPath(cat.category),
                    folderDragSourcePath: undefined,
                    renameableFolderPath: undefined,
                }));
        }

        if (driveLocation.kind === 'category') {
            const categoryNode =
                workspaceTree.find((c) => c.category === driveLocation.categoryKey) ?? null;
            return (categoryNode?.folders ?? [])
                .map((folder) => ({
                    key: `folder:${folder.folder_path}`,
                    name: folder.name,
                    categoryKey: driveLocation.categoryKey,
                    itemCountLabel: folder.count > 0 ? `${folder.count} ${folder.count === 1 ? 'item' : 'items'}` : 'Empty',
                    lastModifiedLabel: folderLastModifiedByPath.get(folder.folder_path)
                        ? formatUploadDate(folderLastModifiedByPath.get(folder.folder_path)!)
                        : '—',
                    folderPath: folder.folder_path,
                    pathLabel: folder.folder_path,
                    onOpen: () => openFolderLocation(folder.folder_path),
                    dropTargetPath: folder.folder_path,
                    folderDragSourcePath: folder.folder_path,
                    renameableFolderPath: folder.folder_path,
                }));
        }

        if (driveLocation.kind === 'folder') {
            const folderNode = currentFolderMatch?.folderNode ?? null;
            return (folderNode?.children ?? []).map((folder) => ({
                key: `folder:${folder.folder_path}`,
                name: folder.name,
                categoryKey: driveLocation.categoryKey,
                itemCountLabel: folder.count > 0 ? `${folder.count} ${folder.count === 1 ? 'item' : 'items'}` : 'Empty',
                lastModifiedLabel: folderLastModifiedByPath.get(folder.folder_path)
                    ? formatUploadDate(folderLastModifiedByPath.get(folder.folder_path)!)
                    : '—',
                folderPath: folder.folder_path,
                pathLabel: folder.folder_path,
                onOpen: () => openFolderLocation(folder.folder_path),
                dropTargetPath: folder.folder_path,
                folderDragSourcePath: folder.folder_path,
                renameableFolderPath: folder.folder_path,
            }));
        }

        return [];
    }, [driveLocation, workspaceTree, categoryLastModifiedByKey, folderLastModifiedByPath, currentFolderMatch, openFolderLocation]);

    const visibleFolderRows = useMemo<DriveFolderRow[]>(() => {
        const search = documentSearchQuery.trim().toLowerCase();
        if (!search) {
            return folderRows;
        }
        return folderRows.filter((folder) => {
            const haystack = [folder.name, folder.pathLabel, folder.itemCountLabel]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return haystack.includes(search);
        });
    }, [documentSearchQuery, folderRows]);

    const tutorialFolderRows = useMemo<DriveFolderRow[]>(() => {
        const today = new Date();
        const rows = [
            { key: 'invoice', name: 'Invoices', itemCountLabel: '5 items', daysAgo: 3 },
            { key: 'bill', name: 'Bills', itemCountLabel: '2 items', daysAgo: 5 },
            { key: 'expense', name: 'Expenses', itemCountLabel: '7 items', daysAgo: 6 },
            { key: 'operations', name: 'Operations', itemCountLabel: '1 item', daysAgo: 7 },
            { key: 'other', name: 'Others', itemCountLabel: '1 item', daysAgo: 12 },
        ];
        return rows.map((row) => ({
            key: `tutorial-folder:${row.key}`,
            name: row.name,
            categoryKey: row.key === 'operations' ? 'other' : row.key,
            itemCountLabel: row.itemCountLabel,
            lastModifiedLabel: formatUploadDate(formatDateKey(addDays(today, -row.daysAgo))),
            folderPath: undefined,
            pathLabel: row.name,
            onOpen: () => undefined,
            dropTargetPath: undefined,
            folderDragSourcePath: undefined,
            renameableFolderPath: undefined,
        }));
    }, []);

    const displayFolderRows = isTutorialDemo ? tutorialFolderRows : visibleFolderRows;
    const displayFiles = isTutorialDemo ? [] : files;
    const displayIsEmpty = isTutorialDemo ? false : isEmpty;
    const displayTotalResults = isTutorialDemo ? tutorialFolderRows.length : totalResults;
    const displayPage = isTutorialDemo ? 1 : page;
    const displayTotalPages = isTutorialDemo ? 1 : Math.max(totalPages, 1);
    const displayPageSize = isTutorialDemo ? tutorialFolderRows.length : pageSize;
    const showPendingReviewSection = !isTutorialDemo && (
        pendingInvoiceDocs.length > 0 ||
        pendingExpenseDocs.length > 0 ||
        pendingBills.length > 0
    );

    const shouldHidePendingWorkspaceClassification = useCallback((file: GlobalFile) => {
        const status = (file.workspace_classification_status || '').trim().toLowerCase();
        if (status === 'pending') {
            return true;
        }
        const folderPath = (file.workspace_folder_path || '').trim();
        return folderPath.length === 0 && autoReclassifyAttemptedRef.current.has(file.file_id);
    }, []);

    // Fetch files for the current drive location. Folder rows are additive UI affordances;
    // they should not hide document rows at the same level.
    const fetchFiles = useCallback(async () => {
        if (!effectiveBusinessId) return;
        const requestSeq = ++fileRequestSeqRef.current;
        try {
            const atFolder = driveLocation.kind === 'folder';
            const atRoot = driveLocation.kind === 'root';
            const atCategory = driveLocation.kind === 'category';
            const categoryNode =
                atCategory
                    ? workspaceTree.find((c) => c.category === driveLocation.categoryKey) ?? null
                    : atFolder
                        ? currentFolderMatch?.categoryNode ?? null
                        : null;
            const hasWorkspaceFolders = workspaceTree.length > 0;
            const hasCurrentFolders =
                atCategory
                    ? (categoryNode?.folders?.length ?? 0) > 0
                    : atFolder
                        ? (currentFolderMatch?.folderNode.children?.length ?? 0) > 0
                        : false;

            const shouldFetchAllRows = atRoot || atCategory || atFolder;
            const query: UserGlobalFilesQuery = {
                page: shouldFetchAllRows ? 1 : page,
                pageSize: shouldFetchAllRows ? 100 : pageSize,
                businessId: effectiveBusinessId,
            };

            if (documentSearchQuery) {
                query.q = documentSearchQuery;
            }

            const res = await documentsApi.getUserGlobalFiles(query);
            let allFiles = res.files;
            let totalPagesFromApi = res.total_pages;
            let totalFromApi = res.total;

            if (shouldFetchAllRows && res.total_pages > 1) {
                const remainingPages = Array.from(
                    { length: res.total_pages - 1 },
                    (_, index) => index + 2,
                );
                const remainingResponses = await Promise.all(
                    remainingPages.map((nextPage) =>
                        documentsApi.getUserGlobalFiles({
                            ...query,
                            page: nextPage,
                        }),
                    ),
                );
                allFiles = [
                    ...res.files,
                    ...remainingResponses.flatMap((response) => response.files),
                ];
                totalPagesFromApi = 1;
                totalFromApi = allFiles.length;
            }
            if (requestSeq !== fileRequestSeqRef.current) return;

            const filesMissingFolderPath = allFiles.filter((file) => {
                if (!file.file_id) return false;
                const folderPath = (file.workspace_folder_path || '').trim();
                return folderPath.length === 0 && !autoReclassifyAttemptedRef.current.has(file.file_id);
            });

            if (filesMissingFolderPath.length > 0) {
                for (const file of filesMissingFolderPath) {
                    autoReclassifyAttemptedRef.current.add(file.file_id);
                }
                void Promise.allSettled(
                    filesMissingFolderPath.map((file) =>
                        documentsApi.reclassifyWorkspaceFile(file.file_id, {
                            businessId: effectiveBusinessId,
                        }),
                    ),
                ).then(() => {
                    refreshWorkspaceFolders();
                    void fetchFiles();
                });
            }

            setPendingSourceFiles(allFiles);
            let visibleFiles = allFiles;

            if (atRoot) {
                visibleFiles = visibleFiles.filter((file) => {
                    if (shouldHidePendingWorkspaceClassification(file)) {
                        return false;
                    }
                    const folderPath = (file.workspace_folder_path || '').trim();
                    if (!folderPath) {
                        return false;
                    }
                    const category = normalizeWorkspaceCategoryForDrive(file.workspace_category);
                    const entity = normalizeWorkspaceEntityForDrive(file.workspace_entity);
                    return !(category === 'other' && entity === 'Unknown');
                });
            }

            if (atRoot && !documentSearchQuery) {
                visibleFiles = [];
            }

            if (atCategory || atFolder) {
                visibleFiles = visibleFiles.filter((file) => !shouldHidePendingWorkspaceClassification(file));
            }

            if (atCategory) {
                const categoryRootPath = categoryNode ? categoryRootFolderPath(categoryNode.category) : '';
                visibleFiles = visibleFiles.filter((file) => {
                    const folderPath = (file.workspace_folder_path || '').trim();
                    if (folderPath === categoryRootPath) {
                        return true;
                    }
                    if (buildComparableWorkspacePath(file) === categoryRootPath) {
                        return true;
                    }
                    return Boolean(documentSearchQuery);
                });
            }

            if (atFolder) {
                visibleFiles = allFiles.filter((file) =>
                    fileMatchesDriveFolderPath(file, driveLocation.folderPath),
                );
                visibleFiles = visibleFiles.filter((file) => !shouldHidePendingWorkspaceClassification(file));
            }

            setQboSyncOverrides((prev) => {
                const next = { ...prev };
                for (const file of allFiles) {
                    if (
                        file.qbo_synced ||
                        file.qbo_sync_status === 'posted' ||
                        file.qbo_sync_status === 'updated' ||
                        file.qbo_sync_status === 'already_exists' ||
                        file.qbo_sync_status === 'failed'
                    ) {
                        delete next[file.file_id];
                    }
                }
                return next;
            });

            setFiles(visibleFiles);
            if (atRoot && hasWorkspaceFolders) {
                setTotalPages(1);
                setTotalResults(visibleFiles.length + visibleFolderRows.length);
            } else if ((atCategory || atFolder) && hasCurrentFolders) {
                setTotalPages(1);
                setTotalResults(visibleFiles.length + visibleFolderRows.length);
            } else if (atFolder) {
                setTotalPages(1);
                setTotalResults(visibleFiles.length);
            } else {
                setTotalPages(totalPagesFromApi);
                setTotalResults(totalFromApi || 0);
            }

            const hasFiles = visibleFiles.length > 0;
            setIsEmpty(!hasFiles);

            const onboardingKey = effectiveBusinessId || '';
            const hasMarkedBaselineAvailable = Boolean(userMetadata?.feature_flags?.available_pl);
            if (hasFiles && onboardingKey && !hasMarkedBaselineAvailable && !onboardingStep3CompletedRef.current.has(onboardingKey)) {
                onboardingStep3CompletedRef.current.add(onboardingKey);
                try {
                    await userMetadataApi.completeOnboardingStep3(effectiveBusinessId);
                } catch (e) {
                    onboardingStep3CompletedRef.current.delete(onboardingKey);
                    console.error("Failed to update onboarding step 3", e);
                }
            }
        } catch (err: any) {
            if (requestSeq !== fileRequestSeqRef.current) return;
            console.error('Failed to load files', err);
            setPendingSourceFiles([]);
        }
    }, [
        page,
        pageSize,
        effectiveBusinessId,
        driveLocation,
        userMetadata?.feature_flags?.available_pl,
        workspaceTree,
        currentFolderMatch,
        normalizeWorkspaceCategoryForDrive,
        normalizeWorkspaceEntityForDrive,
        buildComparableWorkspacePath,
        fileMatchesDriveFolderPath,
        shouldHidePendingWorkspaceClassification,
        documentSearchQuery,
        visibleFolderRows.length,
        refreshWorkspaceFolders,
    ]);

    // Google Sheets OAuth returns to /documents?google_sheets_connected=1 (connect flow lives in AI CFO input).
    useEffect(() => {
        const connected = searchParams.get('google_sheets_connected');
        const err = searchParams.get('google_sheets_error');
        if (connected !== '1' && !err) return;
        if (connected === '1') {
            toast.success('Google account connected');
        }
        if (err) {
            toast.error(decodeURIComponent(err));
        }
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete('google_sheets_connected');
                next.delete('google_sheets_error');
                return next;
            },
            { replace: true },
        );
        void fetchFiles();
    }, [searchParams, setSearchParams, fetchFiles]);

    /** After background Google Sheet re-sync updates Mongo — refresh lists lightly. */
    const onGoogleSheetWorkspaceAutoResync = useCallback(() => {
        void (async () => {
            await refreshWorkspaceFolders();
            await fetchFiles();
        })();
    }, [refreshWorkspaceFolders, fetchFiles]);

    const handleWorkspaceDrop = useCallback(
        async (targetFolderPath: string, payload: WorkspaceDragPayload) => {
            if (!effectiveBusinessId) return;
            const targetCategoryKey = getWorkspaceCategoryKeyForPath(targetFolderPath);
            const sourceCategoryKey =
                payload.sourceCategoryKey
                || (payload.kind === 'folder'
                    ? getWorkspaceCategoryKeyForPath(payload.sourceFolderPath)
                    : normalizeWorkspaceCategoryForDrive(files.find((file) => payload.fileIds.includes(file.file_id))?.workspace_category)
                    || normalizeWorkspaceCategoryForDrive(pendingSourceFiles.find((file) => payload.fileIds.includes(file.file_id))?.workspace_category)
                    || '');
            if (targetCategoryKey && sourceCategoryKey && targetCategoryKey !== sourceCategoryKey) {
                toast.error('You can only move documents within the same document type.');
                return;
            }
            const tid = toast.loading('Moving…');
            try {
                const res = await documentsApi.moveWorkspaceFiles({
                    targetFolderPath,
                    fileIds: payload.kind === 'files' ? payload.fileIds : undefined,
                    sourceFolderPath: payload.kind === 'folder' ? payload.sourceFolderPath : undefined,
                    businessId: effectiveBusinessId,
                });
                toast.dismiss(tid);
                if ((res.updated ?? 0) === 0) {
                    toast(res.message || 'No changes — items may already be in that folder.');
                } else {
                    toast.success(
                        (res.updated ?? 0) === 1
                            ? 'Updated. Syncing files to cloud storage in the background…'
                            : `${res.updated} items updated. Syncing to cloud storage in the background…`,
                    );
                }
                refreshWorkspaceFolders();
                void fetchFiles();
            } catch (e: any) {
                toast.dismiss(tid);
                toast.error(getUserFriendlyErrorMessage(e, 'Could not move item(s).'));
            }
        },
        [effectiveBusinessId, getWorkspaceCategoryKeyForPath, files, pendingSourceFiles, refreshWorkspaceFolders, fetchFiles, normalizeWorkspaceCategoryForDrive],
    );

    const createFolderCategoryOptions = useMemo(
        () => FIXED_WORKSPACE_CATEGORY_KEYS.map((categoryKey) => ({
            key: categoryKey,
            label: categoryLabelForKey(categoryKey),
        })),
        [],
    );

    const handleCreateFolder = useCallback(
        async (name: string, options?: { categoryKeyOverride?: string; parentFolderPathOverride?: string; navigateToCreated?: boolean; selectForOrganize?: boolean }) => {
            if (!effectiveBusinessId) return;
            const trimmedName = name.trim();
            if (!trimmedName) return;
            const categoryKey = options?.categoryKeyOverride
                || (driveLocation.kind === 'category' || driveLocation.kind === 'folder'
                    ? driveLocation.categoryKey
                    : '');
            const parent = options?.parentFolderPathOverride
                || (driveLocation.kind === 'folder'
                    ? driveLocation.folderPath
                    : categoryKey
                        ? categoryRootFolderPath(categoryKey)
                        : '');
            if (!parent) return;
            const tid = toast.loading('Creating folder…');
            try {
                const created = await documentsApi.createWorkspaceFolder({
                    parentFolderPath: parent,
                    name: trimmedName,
                    businessId: effectiveBusinessId,
                });
                await refreshWorkspaceFolders();
                if (options?.selectForOrganize) {
                    setOrganizeTargetPath(created.folder_path);
                    setOrganizeSearch('');
                }
                if (options?.navigateToCreated !== false) {
                    openFolderLocation(created.folder_path);
                }
                toast.dismiss(tid);
                toast.success('Folder created');
                return created.folder_path;
            } catch (e: any) {
                toast.dismiss(tid);
                toast.error(getUserFriendlyErrorMessage(e, 'Could not create folder.'));
            }
        },
        [effectiveBusinessId, driveLocation, refreshWorkspaceFolders, openFolderLocation],
    );

    const createFolderLocationOptions = useMemo(
        () => createFolderCategoryOptions.map((cat) => ({
            key: cat.key,
            label: `Documents / ${cat.label}`,
            parentFolderPath: categoryRootFolderPath(cat.key),
        })),
        [createFolderCategoryOptions],
    );

    const organizeFolderOptions = useMemo(() => {
        const deduped = new Map<string, { folderPath: string; label: string; name: string; categoryKey: string; categoryLabel: string; depth: number }>();
        for (const categoryNode of workspaceTree) {
            const categoryPath = categoryRootFolderPath(categoryNode.category);
            deduped.set(categoryPath, {
                folderPath: categoryPath,
                name: categoryNode.label,
                label: `Documents / ${categoryNode.label}`,
                categoryKey: categoryNode.category,
                categoryLabel: categoryNode.label,
                depth: 0,
            });
            for (const entry of flattenFolderNodes(categoryNode.folders || [])) {
                const fullPath = [
                    'Documents',
                    categoryNode.label,
                    ...entry.ancestors.map((ancestor) => ancestor.name),
                    entry.node.name,
                ].join(' / ');
                deduped.set(entry.node.folder_path, {
                    folderPath: entry.node.folder_path,
                    name: entry.node.name,
                    label: fullPath,
                    categoryKey: categoryNode.category,
                    categoryLabel: categoryNode.label,
                    depth: entry.ancestors.length,
                });
            }
            for (const ent of categoryNode.entities || []) {
                if (deduped.has(ent.folder_path)) continue;
                deduped.set(ent.folder_path, {
                    folderPath: ent.folder_path,
                    name: ent.entity,
                    label: `Documents / ${categoryNode.label} / ${ent.entity}`,
                    categoryKey: categoryNode.category,
                    categoryLabel: categoryNode.label,
                    depth: 1,
                });
            }
        }
        return Array.from(deduped.values()).sort((left, right) => left.label.localeCompare(right.label));
    }, [workspaceTree]);

    const normalizeOrganizePath = useCallback((path?: string | null) => (path || '').trim().replace(/\/+$/, ''), []);
    const organizeSourceCategoryKey = useMemo(() => {
        if (organizeFolderSource) {
            return getWorkspaceCategoryKeyForPath(organizeFolderSource.folderPath);
        }
        if (!organizeSelection.length) return '';
        return normalizeWorkspaceCategoryForDrive(organizeSelection[0]?.workspace_category);
    }, [getWorkspaceCategoryKeyForPath, organizeFolderSource, organizeSelection, normalizeWorkspaceCategoryForDrive]);

    const visibleOrganizeFolderOptions = useMemo(() => {
        const q = organizeSearch.trim().toLowerCase();
        const sourcePath = normalizeOrganizePath(organizeFolderSource?.folderPath);
        const filtered = organizeFolderOptions.filter((option) => {
            if (organizeSourceCategoryKey && option.categoryKey !== organizeSourceCategoryKey) {
                return false;
            }
            const optionPath = normalizeOrganizePath(option.folderPath);
            if (sourcePath) {
                if (optionPath === sourcePath) return false;
                if (optionPath.startsWith(`${sourcePath}/`)) return false;
            }
            if (!q) return true;
            return option.label.toLowerCase().includes(q) || option.name.toLowerCase().includes(q);
        });
        return filtered;
    }, [organizeFolderOptions, organizeSearch, organizeFolderSource, normalizeOrganizePath, organizeSourceCategoryKey]);

    const openOrganizeCategoryLocation = useCallback((categoryKey: string) => {
        const categoryNode = workspaceTree.find((node) => node.category === categoryKey);
        if (!categoryNode) return;
        setOrganizeLocation({
            kind: 'category',
            categoryKey: categoryNode.category,
            categoryLabel: categoryNode.label,
        });
        setOrganizeTargetPath(categoryRootFolderPath(categoryNode.category));
    }, [workspaceTree]);

    const openOrganizeFolderLocation = useCallback((folderPath: string) => {
        const match = findFolderMatchByPath(workspaceTree, folderPath);
        if (!match) return;
        setOrganizeLocation({
            kind: 'folder',
            categoryKey: match.categoryNode.category,
            categoryLabel: match.categoryNode.label,
            folderName: match.folderNode.name,
            folderPath: match.folderNode.folder_path,
            ancestors: match.ancestors,
        });
        setOrganizeTargetPath(match.folderNode.folder_path);
    }, [workspaceTree]);

    const organizeCurrentFolderPath = useMemo(() => {
        if (organizeLocation.kind === 'category') {
            return categoryRootFolderPath(organizeLocation.categoryKey);
        }
        if (organizeLocation.kind === 'folder') {
            return organizeLocation.folderPath;
        }
        return '';
    }, [organizeLocation]);

    const organizeCurrentLabel = useMemo(() => {
        if (organizeLocation.kind === 'root') return 'Documents';
        if (organizeLocation.kind === 'category') return `Documents / ${organizeLocation.categoryLabel}`;
        return organizeLocation.folderPath;
    }, [organizeLocation]);

    const organizeSelectedLabel = useMemo(() => {
        if (!organizeTargetPath) return '';
        const normalizedTarget = normalizeOrganizePath(organizeTargetPath);
        const categoryRootMatch = workspaceTree.find(
            (categoryNode) => normalizeOrganizePath(categoryRootFolderPath(categoryNode.category)) === normalizedTarget,
        );
        if (categoryRootMatch) {
            return `Documents / ${categoryRootMatch.label}`;
        }
        return organizeFolderOptions.find((option) => normalizeOrganizePath(option.folderPath) === normalizedTarget)?.label
            || organizeTargetPath;
    }, [organizeFolderOptions, organizeTargetPath, normalizeOrganizePath, workspaceTree]);

    const organizeAllowsRename = Boolean(organizeFolderSource || organizeSelection.length === 1);
    const organizeNameLabel = organizeFolderSource ? 'Folder name' : 'File name';
    const organizeModalTitle = organizeFolderSource
        ? 'Manage folder'
        : organizeSelection.length === 1
            ? 'Manage file'
            : 'Move files';
    const organizeFileExtension = !organizeFolderSource && organizeSelection.length === 1
        ? splitFilenameForRename(organizeSelection[0]?.original_filename).extension
        : '';
    const categoryKeyForWorkspacePath = useCallback((path?: string | null) => {
        const normalizedPath = normalizeOrganizePath(path);
        if (!normalizedPath) return '';
        const matchedCategory = workspaceTree.find((categoryNode) => {
            const rootPath = normalizeOrganizePath(categoryRootFolderPath(categoryNode.category));
            return normalizedPath === rootPath || normalizedPath.startsWith(`${rootPath}/`);
        });
        return matchedCategory?.category || '';
    }, [normalizeOrganizePath, workspaceTree]);
    const organizeTargetCategoryKey = useMemo(
        () => categoryKeyForWorkspacePath(organizeTargetPath),
        [categoryKeyForWorkspacePath, organizeTargetPath],
    );
    const organizeTypeChangeWarning = useMemo(() => {
        if (!organizeTargetCategoryKey) return '';
        const targetTypeLabel = categoryDocumentTypeLabel(organizeTargetCategoryKey);
        if (!targetTypeLabel) return '';

        if (organizeFolderSource) {
            const sourceCategoryKey = categoryKeyForWorkspacePath(organizeFolderSource.folderPath);
            if (!sourceCategoryKey || sourceCategoryKey === organizeTargetCategoryKey) return '';
            return `Moving this folder to ${targetTypeLabel === 'P&L report' ? 'this category' : organizeSelectedLabel || 'the selected category'} will change the document type for documents inside it to ${targetTypeLabel}.`;
        }

        if (organizeSelection.length === 1) {
            const sourceCategoryKey = normalizeWorkspaceCategoryForDrive(organizeSelection[0]?.workspace_category);
            if (!sourceCategoryKey || sourceCategoryKey === organizeTargetCategoryKey) return '';
            const sourceTypeLabel = categoryDocumentTypeLabel(sourceCategoryKey) || sourceCategoryKey;
            return `This will change the document type from ${sourceTypeLabel} to ${targetTypeLabel}.`;
        }

        const sourceCategoryKeys = Array.from(new Set(
            organizeSelection
                .map((file) => normalizeWorkspaceCategoryForDrive(file.workspace_category))
                .filter(Boolean),
        ));
        if (sourceCategoryKeys.length === 0) return '';
        const willChangeAnyType = sourceCategoryKeys.some((categoryKey) => categoryKey !== organizeTargetCategoryKey);
        if (!willChangeAnyType) return '';
        return `Moving these files here will change their document type to ${targetTypeLabel}.`;
    }, [organizeTargetCategoryKey, organizeFolderSource, organizeSelection, categoryKeyForWorkspacePath, organizeSelectedLabel, normalizeWorkspaceCategoryForDrive]);

    const organizeBrowserRows = useMemo<OrganizeBrowserRow[]>(() => {
        if (organizeLocation.kind === 'root') {
            return workspaceTree
                .filter((categoryNode) => !organizeSourceCategoryKey || categoryNode.category === organizeSourceCategoryKey)
                .map((categoryNode) => ({
                    key: `category:${categoryNode.category}`,
                    name: categoryNode.label,
                    subtitle: `${(categoryNode.folders || []).length} ${(categoryNode.folders || []).length === 1 ? 'folder' : 'folders'}`,
                    childCount: (categoryNode.folders || []).length,
                    onOpen: () => openOrganizeCategoryLocation(categoryNode.category),
                }));
        }

        if (organizeLocation.kind === 'category') {
            const categoryNode = workspaceTree.find((node) => node.category === organizeLocation.categoryKey) || null;
            const folders = (categoryNode?.folders || []).map((folderNode) => ({
                key: `folder:${folderNode.folder_path}`,
                name: folderNode.name,
                subtitle: folderNode.count > 0 ? `${folderNode.count} ${folderNode.count === 1 ? 'item' : 'items'}` : 'Empty',
                childCount: (folderNode.children || []).length,
                onOpen: () => openOrganizeFolderLocation(folderNode.folder_path),
            }));
            const entities = (categoryNode?.entities || [])
                .filter((ent) => !folders.some((f) => f.key === `folder:${ent.folder_path}`))
                .map((ent) => ({
                    key: `entity:${ent.folder_path}`,
                    name: ent.entity,
                    subtitle: ent.count > 0 ? `${ent.count} ${ent.count === 1 ? 'item' : 'items'}` : 'Empty',
                    childCount: 0,
                    onOpen: () => openOrganizeFolderLocation(ent.folder_path),
                }));
            return [...folders, ...entities];
        }

        const currentMatch = findFolderMatchByPath(workspaceTree, organizeLocation.folderPath);
        return (currentMatch?.folderNode.children || []).map((folderNode) => ({
            key: `folder:${folderNode.folder_path}`,
            name: folderNode.name,
            subtitle: folderNode.count > 0 ? `${folderNode.count} ${folderNode.count === 1 ? 'item' : 'items'}` : 'Empty',
            childCount: (folderNode.children || []).length,
            onOpen: () => openOrganizeFolderLocation(folderNode.folder_path),
        }));
    }, [organizeLocation, workspaceTree, openOrganizeCategoryLocation, openOrganizeFolderLocation, organizeSourceCategoryKey]);

    const visibleOrganizeBrowserRows = useMemo(() => {
        const q = organizeSearch.trim().toLowerCase();
        if (!q) return organizeBrowserRows;
        return organizeBrowserRows.filter((row) =>
            `${row.name} ${row.subtitle}`.toLowerCase().includes(q),
        );
    }, [organizeBrowserRows, organizeSearch]);

    const handleOrganizeBack = useCallback(() => {
        if (organizeLocation.kind === 'folder') {
            const previousAncestor = organizeLocation.ancestors[organizeLocation.ancestors.length - 1];
            if (previousAncestor) {
                openOrganizeFolderLocation(previousAncestor.folderPath);
                return;
            }
            setOrganizeLocation({
                kind: 'category',
                categoryKey: organizeLocation.categoryKey,
                categoryLabel: organizeLocation.categoryLabel,
            });
            setOrganizeTargetPath(categoryRootFolderPath(organizeLocation.categoryKey));
            return;
        }
        if (organizeLocation.kind === 'category') {
            if (organizeSourceCategoryKey) {
                return;
            }
            setOrganizeLocation({ kind: 'root' });
            return;
        }
    }, [organizeLocation, openOrganizeFolderLocation, organizeSourceCategoryKey]);

    const handleOpenOrganize = useCallback((selectedFiles: GlobalFile[]) => {
        if (!selectedFiles.length) return;
        setNewFolderParentPathOverride(null);
        setOrganizeFolderSource(null);
        const sourceCategoryKey = normalizeWorkspaceCategoryForDrive(selectedFiles[0]?.workspace_category);
        const sourceCategoryNode = workspaceTree.find((node) => node.category === sourceCategoryKey) || null;
        setOrganizeLocation(sourceCategoryNode
            ? {
                kind: 'category',
                categoryKey: sourceCategoryNode.category,
                categoryLabel: sourceCategoryNode.label,
            }
            : { kind: 'root' });
        setOrganizeSelection(selectedFiles);
        setOrganizeNameDraft(selectedFiles.length === 1 ? splitFilenameForRename(selectedFiles[0]?.original_filename).stem : '');
        const preferredPath = (selectedFiles[0]?.workspace_folder_path || '').trim();
        const fallbackPath =
            organizeFolderOptions.find((option) => option.folderPath === preferredPath && (!sourceCategoryKey || option.categoryKey === sourceCategoryKey))?.folderPath
            || organizeFolderOptions.find((option) => !sourceCategoryKey || option.categoryKey === sourceCategoryKey)?.folderPath
            || '';
        setOrganizeTargetPath(fallbackPath);
        setOrganizeSearch('');
        setOrganizeOpen(true);
    }, [organizeFolderOptions, workspaceTree, normalizeWorkspaceCategoryForDrive]);

    const handleOpenFolderOrganize = useCallback((folderPath: string, name: string) => {
        const sourcePath = normalizeOrganizePath(folderPath);
        if (!sourcePath) return;
        setNewFolderParentPathOverride(null);
        setOrganizeSelection([]);
        setOrganizeFolderSource({ folderPath: sourcePath, name });
        setOrganizeNameDraft(name);
        const sourceCategoryKey = getWorkspaceCategoryKeyForPath(sourcePath);
        const sourceCategoryNode = workspaceTree.find((node) => node.category === sourceCategoryKey) || null;
        setOrganizeLocation(sourceCategoryNode
            ? {
                kind: 'category',
                categoryKey: sourceCategoryNode.category,
                categoryLabel: sourceCategoryNode.label,
            }
            : { kind: 'root' });
        const parentPath = sourcePath.split('/').slice(0, -1).join('/');
        const fallbackPath =
            organizeFolderOptions.find((option) =>
                option.categoryKey === sourceCategoryKey &&
                normalizeOrganizePath(option.folderPath) === parentPath,
            )?.folderPath
            || organizeFolderOptions.find((option) => {
                if (sourceCategoryKey && option.categoryKey !== sourceCategoryKey) return false;
                const optionPath = normalizeOrganizePath(option.folderPath);
                return optionPath !== sourcePath && !optionPath.startsWith(`${sourcePath}/`);
            })?.folderPath
            || '';
        setOrganizeTargetPath(fallbackPath);
        setOrganizeSearch('');
        setOrganizeOpen(true);
    }, [getWorkspaceCategoryKeyForPath, normalizeOrganizePath, organizeFolderOptions, workspaceTree]);

    const handleOpenOrganizeCreateFolder = useCallback(() => {
        if (organizeLocation.kind === 'root') {
            const selectedOption = organizeFolderOptions.find((option) => normalizeOrganizePath(option.folderPath) === normalizeOrganizePath(organizeTargetPath));
            const fallbackCategoryKey = selectedOption?.categoryKey || workspaceTree[0]?.category || '';
            setNewFolderParentPathOverride(null);
            setNewFolderCategoryKey(fallbackCategoryKey);
        } else {
            setNewFolderParentPathOverride(organizeCurrentFolderPath);
        }
        setNewFolderName('');
        setNewFolderOpen(true);
    }, [organizeLocation, organizeFolderOptions, organizeTargetPath, normalizeOrganizePath, organizeCurrentFolderPath, workspaceTree]);

    const handleConfirmOrganize = useCallback(async () => {
        if (!effectiveBusinessId || !organizeTargetPath) return;
        if (!organizeFolderSource && organizeSelection.length === 0) return;
        if (organizeSourceCategoryKey && organizeTargetCategoryKey && organizeSourceCategoryKey !== organizeTargetCategoryKey) {
            toast.error('You can only move documents within the same document type.');
            return;
        }
        const trimmedOrganizeName = organizeNameDraft.trim();
        const tid = toast.loading('Saving changes…');
        try {
            let didRename = false;
            let didMove = false;
            let nextFolderPath = organizeFolderSource?.folderPath || '';
            const normalizedTargetPath = normalizeOrganizePath(organizeTargetPath);

            if (organizeFolderSource) {
                if (trimmedOrganizeName && trimmedOrganizeName !== organizeFolderSource.name) {
                    const renamed = await documentsApi.renameWorkspaceFolder({
                        folderPath: nextFolderPath,
                        newName: trimmedOrganizeName,
                        businessId: effectiveBusinessId,
                    });
                    nextFolderPath = renamed.folder_path || nextFolderPath;
                    didRename = true;
                }

                const currentParentPath = normalizeOrganizePath(nextFolderPath.split('/').slice(0, -1).join('/'));
                if (normalizedTargetPath && normalizedTargetPath !== currentParentPath) {
                    const res = await documentsApi.moveWorkspaceFiles({
                        targetFolderPath: organizeTargetPath,
                        sourceFolderPath: nextFolderPath,
                        businessId: effectiveBusinessId,
                    });
                    nextFolderPath = res.target_folder_path || nextFolderPath;
                    didMove = (res.updated ?? 0) > 0;
                }
            } else {
                const selectedFile = organizeSelection[0];
                if (organizeSelection.length === 1 && selectedFile?.file_id) {
                    const currentFilename = selectedFile.original_filename || '';
                    const { stem: currentStem, extension } = splitFilenameForRename(currentFilename);
                    const nextFilename = trimmedOrganizeName ? `${trimmedOrganizeName}${extension}` : '';
                    if (trimmedOrganizeName && trimmedOrganizeName !== currentStem && nextFilename !== currentFilename) {
                        await documentsApi.updateFile(selectedFile.file_id, { filename: nextFilename }, effectiveBusinessId);
                        didRename = true;
                    }
                }

                const shouldMoveFiles =
                    organizeSelection.length > 1
                    || organizeSelection.some((file) => normalizeOrganizePath(file.workspace_folder_path) !== normalizedTargetPath);
                if (shouldMoveFiles) {
                    const res = await documentsApi.moveWorkspaceFiles({
                        targetFolderPath: organizeTargetPath,
                        fileIds: organizeSelection.map((file) => file.file_id),
                        businessId: effectiveBusinessId,
                    });
                    didMove = (res.updated ?? 0) > 0;
                }
            }
            toast.dismiss(tid);
            if (!didRename && !didMove) {
                toast('No changes to save.');
            } else {
                toast.success(
                    organizeFolderSource
                        ? didRename && didMove
                            ? 'Folder updated. Syncing files to cloud storage in the background…'
                            : didRename
                                ? 'Folder renamed. Syncing files in the background…'
                                : 'Folder moved. Syncing files to cloud storage in the background…'
                        : didRename && didMove
                            ? 'File updated and moved. Syncing to cloud storage in the background…'
                            : didRename
                                ? 'File renamed.'
                                : organizeSelection.length === 1
                                    ? 'Moved. Syncing files to cloud storage in the background…'
                                    : `${organizeSelection.length} files moved. Syncing to cloud storage in the background…`,
                );
            }
            setOrganizeOpen(false);
            setOrganizeSelection([]);
            setOrganizeFolderSource(null);
            setOrganizeNameDraft('');
            setOrganizeTargetPath('');
            await refreshWorkspaceFolders();
            if (organizeFolderSource && nextFolderPath) {
                openFolderLocation(nextFolderPath);
            }
            void fetchFiles();
        } catch (e: any) {
            toast.dismiss(tid);
            toast.error(getUserFriendlyErrorMessage(e, 'Could not save changes.'));
        }
    }, [effectiveBusinessId, organizeTargetPath, organizeSelection, organizeFolderSource, organizeNameDraft, organizeSourceCategoryKey, organizeTargetCategoryKey, refreshWorkspaceFolders, fetchFiles, normalizeOrganizePath, openFolderLocation]);

    const fetchAllInvoiceLikeDocs = useCallback(async () => {
        if (!effectiveBusinessId) {
            setAllInvoiceDocs([]);
            setAllBillDocs([]);
            return;
        }
        try {
            const [invoiceRes, billRes] = await Promise.all([
                documentsApi.listInvoices(undefined, 1, 100, effectiveBusinessId),
                documentsApi.listBills(undefined, 1, 100, effectiveBusinessId),
            ]);
            setAllInvoiceDocs(invoiceRes.invoices || []);
            setAllBillDocs(billRes.invoices || []);
        } catch (err: any) {
            console.error('Failed to load invoice-like documents', err);
            setAllInvoiceDocs([]);
            setAllBillDocs([]);
        }
    }, [effectiveBusinessId]);

    useEffect(() => {
        if (!uploadNotice || uploadNotice.tone === 'processing') return;
        const timeoutId = window.setTimeout(() => setUploadNotice(null), 6000);
        return () => window.clearTimeout(timeoutId);
    }, [uploadNotice]);

    useEffect(() => {
        const jobId = activeUploadJob?.jobId;
        const originalFilename = activeUploadJob?.originalFilename || 'Document';
        const folderPath = activeUploadJob?.folderPath;
        if (!jobId || !effectiveBusinessId) return;

        let cancelled = false;
        let timeoutId: number | null = null;

        const scheduleNextPoll = (delayMs: number) => {
            timeoutId = window.setTimeout(() => {
                if (cancelled) return;
                if (document.visibilityState !== 'visible') {
                    scheduleNextPoll(2000);
                    return;
                }
                void pollStatus();
            }, delayMs);
        };

        const pollStatus = async () => {
            try {
                const status = await documentsApi.getInvoiceUploadStatus(jobId, effectiveBusinessId);
                if (cancelled) return;

                const nextStatus = status.status;
                const documentCount = status.document_count ?? null;
                const processedCount = status.processed_count ?? null;
                const progressLabel =
                    documentCount && documentCount > 1
                        ? `Document ${Math.min((processedCount ?? 0) + 1, documentCount)} of ${documentCount}`
                        : undefined;

                setActiveUploadJob((prev) => prev ? {
                    ...prev,
                    status: nextStatus,
                    message: status.message,
                    documentCount,
                    processedCount,
                    error: status.error,
                } : prev);

                if (nextStatus === 'ready') {
                    setUploadNotice({
                        tone: 'success',
                        title: `${originalFilename} is ready`,
                        detail: folderPath
                            ? `${formatWorkspacePathLabel(folderPath)}${progressLabel ? ` • ${progressLabel}` : ''}`
                            : 'Processing finished.',
                    });
                    setActiveUploadJob(null);
                    void fetchFiles();
                    void fetchAllInvoiceLikeDocs();
                    return;
                }

                if (nextStatus === 'failed') {
                    setUploadNotice({
                        tone: 'error',
                        title: `${originalFilename} could not be processed`,
                        detail: status.error || status.message || 'Processing failed.',
                    });
                    setActiveUploadJob(null);
                    void fetchFiles();
                    void fetchAllInvoiceLikeDocs();
                    return;
                }

                const statusTitle =
                    nextStatus === 'uploaded'
                        ? 'Queued for processing'
                        : nextStatus === 'parsing'
                            ? 'Parsing document'
                            : 'Extracting document data';

                setUploadNotice({
                    tone: 'processing',
                    title: `${originalFilename}: ${statusTitle}`,
                    detail: [folderPath ? formatWorkspacePathLabel(folderPath) : '', progressLabel, status.message]
                        .filter(Boolean)
                        .join(' • '),
                });

                const nextDelay = nextStatus === 'uploaded' ? 4000 : 6000;
                scheduleNextPoll(nextDelay);
            } catch (error) {
                if (cancelled) return;
                console.error('Failed to poll upload job status', error);
                scheduleNextPoll(8000);
            }
        };

        void pollStatus();
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && !cancelled) {
                if (timeoutId) {
                    window.clearTimeout(timeoutId);
                }
                void pollStatus();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            cancelled = true;
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [activeUploadJob?.jobId, activeUploadJob?.originalFilename, activeUploadJob?.folderPath, effectiveBusinessId, fetchAllInvoiceLikeDocs, fetchFiles, formatWorkspacePathLabel]);

    const handleUploadComplete = useCallback(async ({ uploads }: UploadCompletionPayload) => {
        if (!uploads.length) return;

        const primaryUpload = uploads[0];
        const uploadedCount = uploads.length;
        const folderPath = (primaryUpload.workspace_folder_path || '').trim();
        const categoryKey = (primaryUpload.workspace_category || '').trim();
        const location = buildDriveLocationFromPath(folderPath, categoryKey);
        const folderLabel = formatWorkspacePathLabel(folderPath);
        const asyncUpload = uploads.find((upload) => upload.job_id);

        refetchTagLabels();
        await refreshWorkspaceFolders();

        if (location) {
            setDriveLocation(location);
            setPage(1);
        } else {
            void fetchFiles();
        }

        setUploadNotice({
            tone: asyncUpload ? 'processing' : 'success',
            title: uploadedCount === 1
                ? `${primaryUpload.original_filename || 'Document'} uploaded`
                : `${uploadedCount} files uploaded`,
            detail: asyncUpload
                ? `${folderLabel} • Processing started`
                : `Stored in ${folderLabel}`,
        });

        if (asyncUpload?.job_id) {
            setActiveUploadJob({
                jobId: asyncUpload.job_id,
                originalFilename: asyncUpload.original_filename || primaryUpload.original_filename || 'Document',
                folderPath: (asyncUpload.workspace_folder_path || folderPath || '').trim() || undefined,
                status: 'uploaded',
                message: asyncUpload.message,
            });
        } else {
            setActiveUploadJob(null);
            void fetchAllInvoiceLikeDocs();
        }
    }, [buildDriveLocationFromPath, fetchAllInvoiceLikeDocs, fetchFiles, formatWorkspacePathLabel, refreshWorkspaceFolders, refetchTagLabels]);

    useEffect(() => {
        if (!effectiveBusinessId) return;
        void fetchFiles();
        void fetchAllInvoiceLikeDocs();
    }, [fetchFiles, fetchAllInvoiceLikeDocs, effectiveBusinessId]);

    // Auto-refresh only while invoice-like files are still processing.
    const hasProcessingFiles = files.some(
        (f) =>
            isInvoiceLikeType(f.type) &&
            f.processed === false &&
            f.processing_status !== 'failed'
    );
    useEffect(() => {
        if (!hasProcessingFiles || activeUploadJob?.jobId) return;
        const tick = () => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
                return;
            }
            void fetchFiles();
            void fetchAllInvoiceLikeDocs();
        };
        const interval = window.setInterval(() => {
            tick();
        }, 5000);
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                tick();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [activeUploadJob?.jobId, hasProcessingFiles, fetchAllInvoiceLikeDocs, fetchFiles]);

    // Edit Modal ----------------------
    const handleShowEdit = (show: boolean) => {
        setShowEdit(show);
    };
    const handleCloseEdit = () => {
        setShowEdit(false);
    };

    // Pagination ----------------------
    const handleNextPage = () => {
        if (page < totalPages) setPage(page + 1);
    };
    const handlePrevPage = () => {
        if (page > 1) setPage(page - 1);
    };

    // Delete File ----------------------
    const handleRequestDelete = (file: GlobalFile) => {
        setSelectedFile(file);
        setDeleteRelatedRecords(Boolean(isInvoiceLikeType(file.type)));
        setShowDelete(true);
    };
    const handleConfirmDelete = async () => {
        if (!selectedFile) return;
        try {
            if (
                deleteRelatedRecords &&
                isInvoiceLikeType(selectedFile.type)
            ) {
                try {
                    if (normalizeDocumentType(selectedFile.type) === 'bills') {
                        await documentsApi.deleteBillsByFileId(selectedFile.file_id, effectiveBusinessId);
                    } else {
                        await documentsApi.deleteInvoicesByFileId(selectedFile.file_id, effectiveBusinessId);
                    }
                } catch {
                    await documentsApi.updateFile(selectedFile.file_id, { is_deleted: true }, effectiveBusinessId);
                }
            } else {
                await documentsApi.updateFile(selectedFile.file_id, { is_deleted: true }, effectiveBusinessId);
            }
            setShowDelete(false);
            setSelectedFile(null);
            setDeleteRelatedRecords(false);
            fetchFiles();
            fetchAllInvoiceLikeDocs();
        } catch (err: any) {
            alert(getUserFriendlyErrorMessage(err, "Failed to delete document."));
        }
    };

    const handleBulkDelete = async (filesToDelete: GlobalFile[]) => {
        const toConfirm = filesToDelete.length > 1
            ? `Are you sure you want to delete ${filesToDelete.length} documents?`
            : 'Are you sure you want to delete this document?';
        if (!window.confirm(toConfirm)) return;
        try {
            for (const file of filesToDelete) {
                if (isInvoiceLikeType(file.type)) {
                    try {
                        if (normalizeDocumentType(file.type) === 'bills') await documentsApi.deleteBillsByFileId(file.file_id, effectiveBusinessId);
                        else await documentsApi.deleteInvoicesByFileId(file.file_id, effectiveBusinessId);
                    } catch {
                        // Fallback: mark file as deleted if doc not found
                        await documentsApi.updateFile(file.file_id, { is_deleted: true }, effectiveBusinessId);
                    }
                } else {
                    await documentsApi.updateFile(file.file_id, { is_deleted: true }, effectiveBusinessId);
                }
            }
            fetchFiles();
            fetchAllInvoiceLikeDocs();
        } catch (err: any) {
            alert(getUserFriendlyErrorMessage(err, 'Failed to delete one or more documents.'));
        }
    };

    const handleBulkConfirm = async (filesToConfirm: GlobalFile[]) => {
        try {
            if (filesToConfirm.some((file) => {
                const docType = normalizeDocumentType(file.type);
                return docType === 'invoice' || docType === 'bills';
            })) {
                alert('Bulk confirm for invoices and bills is temporarily disabled. Please confirm them individually so QuickBooks writeback can be checked first.');
                return;
            }
            const documentsWithChanges: DocumentPriceIncreases[] = [];
            // Fetch all docs first so we can sort by transaction_date (required for correct price detection order)
            const items: { file: GlobalFile; doc: DocInvoice }[] = [];
            for (const file of filesToConfirm) {
                if (!file.file_id || !isInvoiceLikeType(file.type) || !file.waiting_for_confirmation) continue;
                const docType = normalizeDocumentType(file.type);
                const doc = docType === 'bills'
                    ? await documentsApi.getBillByFileId(file.file_id, effectiveBusinessId)
                    : docType === 'expenses'
                        ? await documentsApi.getExpenseByFileId(file.file_id, effectiveBusinessId)
                        : await documentsApi.getInvoiceByFileId(file.file_id, effectiveBusinessId);
                const docId = doc._id || doc.id;
                if (!docId) continue;
                items.push({ file, doc });
            }
            // Sort by transaction_date ascending so earliest invoice is processed first.
            // Price detection relies on temporal order: later invoices compare to earlier ones.
            items.sort((a, b) => {
                const dateA = a.doc.data?.transaction_date || '';
                const dateB = b.doc.data?.transaction_date || '';
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;
                return dateA.localeCompare(dateB);
            });
            for (const { file, doc } of items) {
                const docId = (doc._id || doc.id)!;
                const docType = normalizeDocumentType(file.type);
                const updateData = {
                    data: {
                        ...doc.data,
                        status: 'confirmed' as const,
                        metadata: doc.data.metadata || {
                            create_time: new Date().toISOString(),
                            last_updated_time: new Date().toISOString(),
                        },
                    },
                };
                let response: DocInvoice;
                if (docType === 'bills') {
                    response = await documentsApi.updateBill(docId, updateData, effectiveBusinessId);
                } else {
                    response = await documentsApi.updateInvoice(docId, updateData, effectiveBusinessId);
                }
                const increases = response?.price_increases ?? [];
                const decreases = response?.price_decreases ?? [];
                if (increases.length > 0 || decreases.length > 0) {
                    documentsWithChanges.push({
                        documentName: file.original_filename || file.filename || 'Document',
                        vendorName: getDocumentVendorName(doc, 'Unknown vendor'),
                        invoiceNumber: doc.data?.invoice_number,
                        items: increases,
                        decreaseItems: decreases.length > 0 ? decreases : undefined,
                    });
                }
            }
            refreshWorkspaceFolders();
            fetchFiles();
            fetchAllInvoiceLikeDocs();
            if (documentsWithChanges.length > 0) {
                setBatchPriceIncreaseDocs(documentsWithChanges);
                setShowBatchPriceIncreaseModal(true);
            }
        } catch (err: any) {
            alert(getUserFriendlyErrorMessage(err, 'Failed to confirm one or more documents.'));
        }
    };
    const handleCancelDelete = () => {
        setShowDelete(false);
        setSelectedFile(null);
        setDeleteRelatedRecords(false);
    };

    const handleShowChat = () => {
        setShowChat(true)
    }

    const buildQboSyncUpdateData = (doc: DocInvoice): UpdateInvoiceRequest => ({
        data: {
            ...doc.data,
            metadata: doc.data.metadata || {
                create_time: new Date().toISOString(),
                last_updated_time: new Date().toISOString(),
            },
        },
    });

    const fetchInvoiceLikeDocumentByFile = async (file: GlobalFile): Promise<DocInvoice> => {
        const docType = normalizeDocumentType(file.type);
        if (file.linked_document_id) {
            if (docType === 'bills') {
                return documentsApi.getBill(file.linked_document_id, effectiveBusinessId);
            }
            if (docType === 'expenses') {
                return documentsApi.getExpense(file.linked_document_id, effectiveBusinessId);
            }
            return documentsApi.getInvoice(file.linked_document_id, effectiveBusinessId);
        }
        if (docType === 'bills') {
            return documentsApi.getBillByFileId(file.file_id, effectiveBusinessId);
        }
        if (docType === 'expenses') {
            return documentsApi.getExpenseByFileId(file.file_id, effectiveBusinessId);
        }
        return documentsApi.getInvoiceByFileId(file.file_id, effectiveBusinessId);
    };

    const handleCloseQboSyncModal = () => {
        setShowQboSyncModal(false);
        setSelectedQboSyncFile(null);
        setSelectedQboSyncDoc(null);
        setSelectedQboSyncType(null);
        setQboSyncError(null);
    };

    const refreshInvoiceWorkflowState = useCallback((
        fileId?: string | null,
        noticeTitle: string = 'Document confirmed',
    ) => {
        void refreshWorkspaceFolders();
        void fetchFiles();
        void fetchAllInvoiceLikeDocs();
        if (fileId) {
            void revealLatestFileLocation(fileId, 350).then((latestFile) => {
                if (!latestFile || typeof latestFile !== 'object') return;
                const folderLabel = formatWorkspacePathLabel(latestFile.workspace_folder_path);
                if (activeUploadJob?.jobId) return;
                setUploadNotice({
                    tone: 'success',
                    title: noticeTitle,
                    detail: `Stored in ${folderLabel}`,
                });
            });
        }
        window.setTimeout(() => {
            void refreshWorkspaceFolders();
            void fetchFiles();
            void fetchAllInvoiceLikeDocs();
            if (fileId) {
                void revealLatestFileLocation(fileId).then((latestFile) => {
                    if (!latestFile || typeof latestFile !== 'object') return;
                    const folderLabel = formatWorkspacePathLabel(latestFile.workspace_folder_path);
                    if (activeUploadJob?.jobId) return;
                    setUploadNotice({
                        tone: 'success',
                        title: noticeTitle,
                        detail: `Stored in ${folderLabel}`,
                    });
                });
            }
        }, 1200);
    }, [activeUploadJob?.jobId, fetchAllInvoiceLikeDocs, fetchFiles, formatWorkspacePathLabel, refreshWorkspaceFolders, revealLatestFileLocation]);

    const applyConfirmedDocumentResponse = useCallback((response: DocInvoice | null | undefined, noticeTitle: string) => {
        if (!response) return;
        const folderPath = (response.workspace_folder_path || '').trim();
        if (!folderPath) return;
        const location = buildDriveLocationFromPath(folderPath, response.workspace_category);
        if (location) {
            setDriveLocation(location);
            setPage(1);
        }
        if (!activeUploadJob?.jobId) {
            setUploadNotice({
                tone: 'success',
                title: noticeTitle,
                detail: `Stored in ${formatWorkspacePathLabel(folderPath)}`,
            });
        }
    }, [activeUploadJob?.jobId, buildDriveLocationFromPath, formatWorkspacePathLabel]);

    const handleRequestQboSync = async (file: GlobalFile) => {
        const docType = normalizeDocumentType(file.type);
        if (!file.file_id || (docType !== 'invoice' && docType !== 'bills')) return;
        setSelectedQboSyncFile(file);
        setSelectedQboSyncDoc(null);
        setSelectedQboSyncType(null);
        setShowQboSyncModal(false);
        setQboSyncLoading(true);
        setQboSyncError(null);
        try {
            const doc = await fetchInvoiceLikeDocumentByFile(file);
            setSelectedQboSyncFile(file);
            setSelectedQboSyncDoc(doc);
            setSelectedQboSyncType(docType);
            setShowQboSyncModal(true);
        } catch (err: any) {
            setQboSyncError(getUserFriendlyErrorMessage(err, 'Failed to prepare QuickBooks sync.'));
            handleCloseQboSyncModal();
        } finally {
            setQboSyncLoading(false);
        }
    };

    const handleConfirmQboSync = useCallback(async () => {
        if (!selectedQboSyncDoc || !selectedQboSyncType) return;
        const docId = getDocId(selectedQboSyncDoc);
        if (!docId) return;
        setQboSyncLoading(true);
        setQboSyncError(null);
        try {
            if (selectedQboSyncType === 'bills') {
                await documentsApi.queueBillQboSync(docId, effectiveBusinessId);
            } else {
                await documentsApi.queueInvoiceQboSync(docId, effectiveBusinessId);
            }
            if (selectedQboSyncFile?.file_id) {
                setQboSyncOverrides((prev) => ({
                    ...prev,
                    [selectedQboSyncFile.file_id]: {
                        qbo_sync_status: 'queued',
                        qbo_synced: false,
                    },
                }));
            }
            handleCloseQboSyncModal();
            refreshInvoiceWorkflowState(selectedQboSyncFile?.file_id, 'QuickBooks sync queued');
        } catch (err: any) {
            setQboSyncError(getUserFriendlyErrorMessage(err, 'Failed to sync document to QuickBooks.'));
        } finally {
            setQboSyncLoading(false);
        }
    }, [effectiveBusinessId, refreshInvoiceWorkflowState, selectedQboSyncDoc, selectedQboSyncFile, selectedQboSyncType]);

    const handleInvoiceClick = (invoice: DocInvoice) => {
        const docId = getDocId(invoice);
        if (!invoice || !docId) {
            alert('Document data is invalid. Please refresh the page.');
            return;
        }

        setSelectedInvoice(invoice);
        setSelectedInvoicePreview({});
        setShowInvoiceModal(true);
    };

    const handleInvoiceConfirm = (response?: DocInvoice) => {
        const confirmedFileId = getPrimaryDocFileId(response || selectedInvoice);
        const noticeTitle = selectedInvoice?.document_type === 'expense' ? 'Expense confirmed' : 'Invoice confirmed';
        setShowInvoiceModal(false);
        setSelectedInvoice(null);
        setSelectedInvoicePreview({});
        applyConfirmedDocumentResponse(response, noticeTitle);
        refreshInvoiceWorkflowState(confirmedFileId, noticeTitle);
    };
    const handleInvoiceCancel = () => {
        setShowInvoiceModal(false);
        setSelectedInvoice(null);
        setSelectedInvoicePreview({});
        fetchAllInvoiceLikeDocs();
    };

    const handleBillClick = (bill: DocInvoice) => {
        const docId = getDocId(bill);
        if (!bill || !docId) {
            alert('Bill data is invalid. Please refresh the page.');
            return;
        }
        setSelectedBill(bill);
        setSelectedBillPreview({});
        setShowBillModal(true);
    };
    const handleBillConfirm = (response?: DocInvoice) => {
        const confirmedFileId = getPrimaryDocFileId(response || selectedBill);
        setShowBillModal(false);
        setSelectedBill(null);
        setSelectedBillPreview({});
        applyConfirmedDocumentResponse(response, 'Bill confirmed');
        refreshInvoiceWorkflowState(confirmedFileId, 'Bill confirmed');
    };
    const handleBillCancel = () => {
        setShowBillModal(false);
        setSelectedBill(null);
        setSelectedBillPreview({});
        fetchAllInvoiceLikeDocs();
    };

    // Click invoice/expense/bill file in Documents list → fetch doc by file_id and open confirmation modal
    // Fallback: if API fails (doc not ready yet), check pending list—banner may have it before file shows "ready"
    const invoiceFileClickInProgress = useRef(false);
    const handleInvoiceFileClick = async (file: GlobalFile) => {
        if (!file.file_id || !file.type) return;
        if (!isInvoiceLikeType(file.type)) return;
        if (invoiceFileClickInProgress.current) return;
        invoiceFileClickInProgress.current = true;
        try {
            const docType = normalizeDocumentType(file.type);
            const doc = file.linked_document_id
                ? (
                    docType === 'bills'
                        ? await documentsApi.getBill(file.linked_document_id, effectiveBusinessId)
                        : docType === 'expenses'
                            ? await documentsApi.getExpense(file.linked_document_id, effectiveBusinessId)
                            : await documentsApi.getInvoice(file.linked_document_id, effectiveBusinessId)
                )
                : docType === 'bills'
                    ? await documentsApi.getBillByFileId(file.file_id, effectiveBusinessId)
                    : docType === 'expenses'
                        ? await documentsApi.getExpenseByFileId(file.file_id, effectiveBusinessId)
                        : await documentsApi.getInvoiceByFileId(file.file_id, effectiveBusinessId);
            if (docType === 'bills') {
                setSelectedBill(doc);
                setSelectedBillPreview({});
                setShowBillModal(true);
            } else {
                setSelectedInvoice(doc);
                setSelectedInvoicePreview({});
                setShowInvoiceModal(true);
            }
        } catch (err: any) {
            // Fallback: doc may exist in pending list before user_global_files is updated
            const pending = normalizeDocumentType(file.type) === 'bills' ? pendingBills : pendingInvoices;
            const matched = pending.find((p: DocInvoice) => p.file_id === file.file_id);
            if (matched) {
                if (normalizeDocumentType(file.type) === 'bills') {
                    setSelectedBill(matched);
                    setSelectedBillPreview({});
                    setShowBillModal(true);
                } else {
                    setSelectedInvoice(matched);
                    setSelectedInvoicePreview({});
                    setShowInvoiceModal(true);
                }
            } else {
                alert(err.message || 'Could not load document. Processing may still be in progress—please use the pending review banner above when it appears, or wait and try again.');
            }
        } finally {
            invoiceFileClickInProgress.current = false;
        }
    };

    useEffect(() => {
        if (!targetFileId) return;
        if (!effectiveBusinessId) return;

        const targetKey = `${targetFileId}:${targetDocType || 'auto'}:${effectiveBusinessId}`;
        if (handledDeepLinkTarget === targetKey) return;

        let mounted = true;

        const clearTargetParams = () => {
            const next = new URLSearchParams(searchParamsString);
            next.delete('target_file_id');
            next.delete('target_doc_type');
            next.delete('target_business_id');
            setSearchParams(next, { replace: true });
        };

        const openInvoiceByFileId = async () => {
            const matchingFileRow = files.find((file) => file.file_id === targetFileId && file.linked_document_id);
            if (matchingFileRow?.linked_document_id) {
                const invoice = await documentsApi.getInvoice(matchingFileRow.linked_document_id, effectiveBusinessId);
                if (!mounted) return;
                setSelectedInvoice(invoice);
                setShowInvoiceModal(true);
                return;
            }
            const invoice = await documentsApi.getInvoiceByFileId(targetFileId, effectiveBusinessId);
            if (!mounted) return;
            setSelectedInvoice(invoice);
            setShowInvoiceModal(true);
        };

        const openExpenseByFileId = async () => {
            const matchingFileRow = files.find((file) => file.file_id === targetFileId && file.linked_document_id);
            if (matchingFileRow?.linked_document_id) {
                const expense = await documentsApi.getExpense(matchingFileRow.linked_document_id, effectiveBusinessId);
                if (!mounted) return;
                setSelectedInvoice(expense);
                setShowInvoiceModal(true);
                return;
            }
            const expense = await documentsApi.getExpenseByFileId(targetFileId, effectiveBusinessId);
            if (!mounted) return;
            setSelectedInvoice(expense);
            setShowInvoiceModal(true);
        };

        const openBillByFileId = async () => {
            const matchingFileRow = files.find((file) => file.file_id === targetFileId && file.linked_document_id);
            if (matchingFileRow?.linked_document_id) {
                const bill = await documentsApi.getBill(matchingFileRow.linked_document_id, effectiveBusinessId);
                if (!mounted) return;
                setSelectedBill(bill);
                setShowBillModal(true);
                return;
            }
            const bill = await documentsApi.getBillByFileId(targetFileId, effectiveBusinessId);
            if (!mounted) return;
            setSelectedBill(bill);
            setShowBillModal(true);
        };

        const openTarget = async () => {
            try {
                if (targetDocType === 'invoice') {
                    await openInvoiceByFileId();
                    return;
                }
                if (targetDocType === 'expenses') {
                    await openExpenseByFileId();
                    return;
                }
                if (targetDocType === 'bills') {
                    await openBillByFileId();
                    return;
                }
                try {
                    await openInvoiceByFileId();
                } catch {
                    try {
                        await openExpenseByFileId();
                    } catch {
                        await openBillByFileId();
                    }
                }
            } catch (err: any) {
                if (mounted) {
                    alert(err?.message || 'Could not open the target document.');
                }
            } finally {
                if (mounted) {
                    setHandledDeepLinkTarget(targetKey);
                    clearTargetParams();
                }
            }
        };

        openTarget();

        return () => {
            mounted = false;
        };
    }, [
        effectiveBusinessId,
        handledDeepLinkTarget,
        searchParamsString,
        setSearchParams,
        targetDocType,
        targetFileId,
    ]);

    return (
        <div
            className={`flex h-full overflow-hidden relative ${isAdminEmbedded ? 'bg-transparent' : 'bg-white'}`}
            onDragEnter={handlePageDragEnter}
            onDragOver={handlePageDragOver}
            onDragLeave={handlePageDragLeave}
            onDrop={handlePageDrop}
        >
            {pageDragActive && (
                <div className="absolute inset-0 z-40 pointer-events-none bg-indigo-500/10 backdrop-blur-[1px]">
                    <div className="absolute inset-6 rounded-2xl border-2 border-dashed border-indigo-500 bg-white/80 flex items-center justify-center">
                        <div className="text-center px-6">
                            <div className="text-xl font-semibold text-gray-900">Drop documents to upload</div>
                            <div className="mt-2 text-sm text-gray-600">
                                PDF, JPG, PNG, HEIC, CSV, XLS, and XLSX files are supported.
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className={`flex flex-col h-full overflow-hidden overflow-x-hidden scrollbar-hide flex-1 transition-all duration-300 relative ${showChat && !showSetupGuidePanel && !isMobile ? 'w-[70%]' : 'w-full'}`}>
                <div className="relative flex-1 pb-4 overflow-hidden flex flex-col min-h-0">
                    {/* Edit Modal */}
                    {showEdit && (
                        <div className="z-[2000]">
                            <DocEdit handleCloseEdit={handleCloseEdit} />
                        </div>
                    )}

                    {/* Invoice/Bill Confirmation Modal */}
                    {showInvoiceModal && selectedInvoice && getDocId(selectedInvoice) && (
                        <InvoiceConfirmationModal
                            documentType={selectedInvoice.document_type === 'expense' ? 'expenses' : 'invoice'}
                            documentId={getDocId(selectedInvoice)!}
                            preFetchedInvoice={selectedInvoice}
                            invoiceData={undefined}
                            sourceFilename={selectedInvoicePreview.filename || (selectedInvoice.file_id ? files.find((file) => file.file_id === selectedInvoice.file_id)?.filename : undefined)}
                            sourcePreviewPage={selectedInvoicePreview.previewPage}
                            sourcePreviewPages={selectedInvoicePreview.previewPages}
                            forceSinglePreviewSource={Boolean(selectedInvoicePreview.forceSingle)}
                            onClose={handleInvoiceCancel}
                            onConfirm={handleInvoiceConfirm}
                            businessId={effectiveBusinessId}
                            enableQboPrompt={false}
                        />
                    )}
                    {showBillModal && selectedBill && getDocId(selectedBill) && (
                        <InvoiceConfirmationModal
                            documentType="bills"
                            documentId={getDocId(selectedBill)!}
                            preFetchedInvoice={selectedBill}
                            invoiceData={undefined}
                            sourceFilename={selectedBillPreview.filename || (selectedBill.file_id ? files.find((file) => file.file_id === selectedBill.file_id)?.filename : undefined)}
                            sourcePreviewPage={selectedBillPreview.previewPage}
                            sourcePreviewPages={selectedBillPreview.previewPages}
                            forceSinglePreviewSource={Boolean(selectedBillPreview.forceSingle)}
                            onClose={handleBillCancel}
                            onConfirm={handleBillConfirm}
                            businessId={effectiveBusinessId}
                            enableQboPrompt={false}
                        />
                    )}

                    {showQboSyncModal && selectedQboSyncType && (
                        <div className="fixed inset-0 z-[3001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-6">
                            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col">
                                <div className="p-6 border-b border-gray-200">
                                    <div className="flex items-center gap-3 text-blue-700">
                                        <AlertCircle className="w-8 h-8 shrink-0" />
                                        <div>
                                            <h3 className="text-lg font-semibold">Write This {selectedQboSyncType === 'bills' ? 'Bill' : 'Invoice'} To QuickBooks As a Bill?</h3>
                                            <p className="text-sm text-gray-600 mt-1">
                                                This will queue a background QuickBooks sync. You can leave this page while it runs.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="px-6 py-5 text-sm text-gray-700 space-y-2">
                                    {selectedQboSyncDoc ? (
                                        <>
                                            <div className="flex items-center justify-between gap-4">
                                                <span className="text-gray-500">Bill Number</span>
                                                <span className="font-medium text-gray-900">{selectedQboSyncDoc.data.invoice_number || 'None'}</span>
                                            </div>
                                            <div className="flex items-center justify-between gap-4">
                                                <span className="text-gray-500">{selectedQboSyncType === 'invoice' ? 'Customer' : 'Vendor'}</span>
                                                <span className="font-medium text-gray-900">{getDocumentVendorName(selectedQboSyncDoc, selectedQboSyncType === 'invoice' ? 'Unknown Customer' : 'Unknown Vendor')}</span>
                                            </div>
                                            <div className="flex items-center justify-between gap-4">
                                                <span className="text-gray-500">Total Amount</span>
                                                <span className="font-medium text-gray-900">${(selectedQboSyncDoc.data.total_amount || 0).toFixed(2)}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-gray-600">
                                            Loading document details...
                                        </div>
                                    )}
                                    {selectedQboSyncFile?.original_filename && (
                                        <div className="flex items-center justify-between gap-4">
                                            <span className="text-gray-500">Document</span>
                                            <span className="font-medium text-gray-900">{selectedQboSyncFile.original_filename}</span>
                                        </div>
                                    )}
                                    {qboSyncError && (
                                        <div className="rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">
                                            {qboSyncError}
                                        </div>
                                    )}
                                </div>
                                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={handleCloseQboSyncModal}
                                        className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                                        disabled={qboSyncLoading}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleConfirmQboSync}
                                        className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                        disabled={!selectedQboSyncDoc || qboSyncLoading}
                                    >
                                        {qboSyncLoading
                                            ? (selectedQboSyncDoc ? 'Queueing…' : 'Loading…')
                                            : 'Yes, Sync in Background'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Batch Price Increase Modal — shown after batch confirm when any doc had increases */}
                    {showBatchPriceIncreaseModal && batchPriceIncreaseDocs.length > 0 && (
                        <BatchPriceIncreaseModal
                            documents={batchPriceIncreaseDocs}
                            onClose={() => {
                                setShowBatchPriceIncreaseModal(false);
                                setBatchPriceIncreaseDocs([]);
                            }}
                        />
                    )}

                    {newFolderOpen && createPortal(
                        <div className="modal-container">
                            <div className="relative mx-4 w-[calc(100vw-2rem)] max-w-lg rounded-2xl bg-white p-5 shadow-xl sm:mx-0 sm:w-full sm:p-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setNewFolderOpen(false);
                                        setNewFolderName('');
                                        setNewFolderParentPathOverride(null);
                                    }}
                                    className="absolute right-4 top-4 text-gray-500 hover:text-black"
                                    aria-label="Close create folder dialog"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                                <div className="mb-5">
                                    <h2 className="text-lg font-semibold text-gray-900">New folder</h2>
                                    <p className="mt-1 text-sm text-gray-500">
                                        Choose where the folder should appear in Documents.
                                    </p>
                                </div>
                                <div className="space-y-4">
                                    <label className="block">
                                        <span className="mb-1.5 block text-sm font-medium text-gray-700">Folder name</span>
                                        <input
                                            type="text"
                                            autoFocus
                                            placeholder="Untitled folder"
                                            value={newFolderName}
                                            onChange={(e) => setNewFolderName(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Escape') {
                                                    setNewFolderOpen(false);
                                                    setNewFolderName('');
                                                    setNewFolderParentPathOverride(null);
                                                    return;
                                                }
                                                if (e.key === 'Enter' && newFolderName.trim()) {
                                                    void Promise.resolve(handleCreateFolder(
                                                        newFolderName.trim(),
                                                        {
                                                            categoryKeyOverride: driveLocation.kind === 'folder' ? undefined : newFolderCategoryKey,
                                                            parentFolderPathOverride: newFolderParentPathOverride || undefined,
                                                            navigateToCreated: !newFolderParentPathOverride,
                                                            selectForOrganize: Boolean(newFolderParentPathOverride),
                                                        },
                                                    )).finally(() => {
                                                        setNewFolderOpen(false);
                                                        setNewFolderName('');
                                                        setNewFolderParentPathOverride(null);
                                                    });
                                                }
                                            }}
                                            className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/20"
                                        />
                                    </label>
                                    {newFolderParentPathOverride ? (
                                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                                            Create inside: {newFolderParentPathOverride}
                                        </div>
                                    ) : driveLocation.kind !== 'folder' ? (
                                        <label className="block">
                                            <span className="mb-1.5 block text-sm font-medium text-gray-700">Location</span>
                                            <select
                                                value={newFolderCategoryKey}
                                                onChange={(e) => setNewFolderCategoryKey(e.target.value)}
                                                className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/20"
                                            >
                                                {createFolderLocationOptions.map((option) => (
                                                    <option key={option.key} value={option.key}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    ) : (
                                        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                                            Create inside: {driveLocation.folderPath}
                                        </div>
                                    )}
                                </div>
                                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setNewFolderOpen(false);
                                            setNewFolderName('');
                                            setNewFolderParentPathOverride(null);
                                        }}
                                        className="w-full rounded-lg px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 sm:w-auto"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const n = newFolderName.trim();
                                            if (!n) return;
                                            void Promise.resolve(handleCreateFolder(
                                                n,
                                                {
                                                    categoryKeyOverride: driveLocation.kind === 'folder' ? undefined : newFolderCategoryKey,
                                                    parentFolderPathOverride: newFolderParentPathOverride || undefined,
                                                    navigateToCreated: !newFolderParentPathOverride,
                                                    selectForOrganize: Boolean(newFolderParentPathOverride),
                                                },
                                            )).finally(() => {
                                                setNewFolderOpen(false);
                                                setNewFolderName('');
                                                setNewFolderParentPathOverride(null);
                                            });
                                        }}
                                        disabled={!newFolderName.trim() || (!newFolderParentPathOverride && driveLocation.kind !== 'folder' && !newFolderCategoryKey)}
                                        className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40 sm:w-auto"
                                    >
                                        Create folder
                                    </button>
                                </div>
                            </div>
                        </div>,
                        document.body
                    )}

                    {organizeOpen && createPortal(
                        <div className="modal-container">
                            <div className="relative mx-4 flex h-[min(80vh,720px)] w-[calc(100vw-2rem)] max-w-2xl flex-col overflow-hidden rounded-2xl bg-white p-5 shadow-xl sm:mx-0 sm:w-full sm:p-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setOrganizeOpen(false);
                                        setOrganizeSelection([]);
                                        setOrganizeFolderSource(null);
                                        setOrganizeNameDraft('');
                                        setOrganizeSearch('');
                                        setOrganizeTargetPath('');
                                        setNewFolderParentPathOverride(null);
                                    }}
                                    className="absolute right-4 top-4 text-gray-500 hover:text-black"
                                    aria-label="Close organize dialog"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                                <div className="mb-5">
                                    <h2 className="text-lg font-semibold text-gray-900">{organizeModalTitle}</h2>
                                    <p className="mt-1 text-sm text-gray-500">
                                        {organizeFolderSource
                                            ? `Rename or move "${organizeFolderSource.name}".`
                                            : organizeSelection.length === 1
                                                ? `Rename or move "${organizeSelection[0]?.original_filename || 'this file'}".`
                                                : `Choose the destination folder for ${organizeSelection.length} selected ${organizeSelection.length === 1 ? 'file' : 'files'}.`}
                                    </p>
                                </div>
                                <div className="flex min-h-0 flex-1 flex-col gap-4">
                                    {organizeAllowsRename && (
                                        <label className="block">
                                            <span className="mb-1.5 block text-sm font-medium text-gray-700">{organizeNameLabel}</span>
                                            <input
                                                type="text"
                                                value={organizeNameDraft}
                                                onChange={(e) => setOrganizeNameDraft(e.target.value)}
                                                placeholder={organizeNameLabel}
                                                className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/20"
                                            />
                                        </label>
                                    )}
                                    <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                                        <div className="min-w-0">
                                            <div className="text-xs font-medium text-gray-500">
                                                Current level
                                                {organizeTypeChangeWarning && (
                                                    <span className="ml-2 text-[11px] font-normal text-amber-700">
                                                        ({organizeTypeChangeWarning})
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-1 flex min-w-0 items-center gap-2 text-sm text-gray-900">
                                                {organizeLocation.kind !== 'root' && (
                                                    <button
                                                        type="button"
                                                        onClick={handleOrganizeBack}
                                                        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                                                        aria-label="Go back"
                                                    >
                                                        <ChevronLeft className="h-4 w-4" />
                                                    </button>
                                                )}
                                                <span className="truncate font-medium">{organizeCurrentLabel}</span>
                                            </div>
                                        </div>
                                        {!organizeSourceCategoryKey && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setOrganizeLocation({ kind: 'root' });
                                                    setOrganizeSearch('');
                                                }}
                                                className="shrink-0 rounded-md px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-white"
                                            >
                                                Root
                                            </button>
                                        )}
                                    </div>
                                    <div className="flex items-end gap-2">
                                        <label className="block min-w-0 flex-1">
                                            <span className="mb-1.5 block text-sm font-medium text-gray-700">
                                                {organizeLocation.kind === 'root' ? 'Search top-level folders' : 'Search this level'}
                                            </span>
                                            <input
                                                type="search"
                                                value={organizeSearch}
                                                onChange={(e) => setOrganizeSearch(e.target.value)}
                                                placeholder={organizeLocation.kind === 'root' ? 'Search top-level folders' : 'Search folders in this level'}
                                                className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-400/20"
                                            />
                                        </label>
                                        {MANUAL_EMPTY_FOLDERS_ENABLED && (
                                            <button
                                                type="button"
                                                onClick={handleOpenOrganizeCreateFolder}
                                                disabled={workspaceTree.length === 0}
                                                className="inline-flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-40"
                                                aria-label={organizeLocation.kind === 'root' ? 'Create folder under a top-level category' : 'Create folder in current level'}
                                                title="Create folder"
                                            >
                                                <FolderPlus className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                    <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                                        <div className="h-full overflow-y-auto">
                                            {visibleOrganizeBrowserRows.length === 0 ? (
                                                <div className="px-4 py-8 text-center text-sm text-gray-500">No matching folders.</div>
                                            ) : (
                                                <div className="divide-y divide-gray-200">
                                                    {visibleOrganizeBrowserRows.map((row) => (
                                                        <button
                                                            key={row.key}
                                                            type="button"
                                                            onClick={row.onOpen}
                                                            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-white"
                                                        >
                                                            <span className="min-w-0">
                                                                <span className="flex items-center gap-2 font-medium text-gray-900">
                                                                    <Folder className="h-4 w-4 shrink-0 text-amber-600" />
                                                                    <span className="truncate">{row.name}</span>
                                                                </span>
                                                                <span className="mt-0.5 block truncate text-xs text-gray-500">{row.subtitle}</span>
                                                            </span>
                                                            <span className="inline-flex items-center gap-2 text-xs text-gray-400">
                                                                {row.childCount > 0 ? `${row.childCount} ${row.childCount === 1 ? 'subfolder' : 'subfolders'}` : 'Open'}
                                                                <ChevronRight className="h-4 w-4" />
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {/* <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 bg-white px-4 py-3">
                                        <div className="text-xs font-medium uppercase tracking-[0.08em] text-gray-500">Selected destination</div>
                                        <div className="mt-1 text-sm font-medium">
                                            {organizeSelectedLabel || 'Open a folder level to choose where to organize'}
                                        </div>
                                        {organizeTypeChangeWarning && (
                                            <div className="mt-1.5 text-sm text-amber-700">
                                                {organizeTypeChangeWarning}
                                            </div>
                                        )}
                                    </div> */}
                                </div>
                                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setOrganizeOpen(false);
                                            setOrganizeSelection([]);
                                            setOrganizeFolderSource(null);
                                            setOrganizeNameDraft('');
                                            setOrganizeSearch('');
                                            setOrganizeTargetPath('');
                                            setNewFolderParentPathOverride(null);
                                        }}
                                        className="w-full rounded-lg px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 sm:w-auto"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleConfirmOrganize()}
                                        disabled={!organizeTargetPath || (!organizeFolderSource && organizeSelection.length === 0)}
                                        className="w-full rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40 sm:w-auto"
                                    >
                                        {organizeAllowsRename ? 'Save changes' : 'Move here'}
                                    </button>
                                </div>
                            </div>
                        </div>,
                        document.body
                    )}

                    {/* Delete Modal */}
                    {showDelete && selectedFile && createPortal(
                        <div className="modal-container">
                            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative text-center">
                                <button
                                    onClick={handleCancelDelete}
                                    className="absolute top-4 right-4 text-gray-500 hover:text-black"
                                >
                                    ✕
                                </button>
                                <h2 className="text-md font-medium my-10">
                                    Are you sure you want to delete this document?
                                </h2>
                                {isInvoiceLikeType(selectedFile.type) && (
                                    <label className="mb-6 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left">
                                        <input
                                            type="checkbox"
                                            checked={deleteRelatedRecords}
                                            onChange={(e) => setDeleteRelatedRecords(e.target.checked)}
                                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-700">
                                            Delete all related records too
                                            <span className="block text-xs text-gray-500 mt-1">
                                                Remove the linked {normalizeDocumentType(selectedFile.type) === 'bills' ? 'bill' : normalizeDocumentType(selectedFile.type) === 'expenses' ? 'expense' : 'invoice'} record too. Unchecked: hide only this document from the documents list.
                                            </span>
                                        </span>
                                    </label>
                                )}
                                <div className="flex justify-center gap-4">
                                    <button
                                        onClick={handleCancelDelete}
                                        className="button-2 w-full sm:w-36"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleConfirmDelete}
                                        className="w-full sm:w-auto min-w-[160px] rounded-lg bg-red-600 px-5 py-3 text-base font-medium text-white hover:bg-red-700 transition-colors"
                                    >
                                        Delete document
                                    </button>
                                </div>
                            </div>
                        </div>,
                        document.body
                    )}

                    {/* Main Section */}
                    <div className={`main-section-container flex flex-col flex-1 min-h-0 p-0 relative ${isAdminEmbedded ? '!bg-transparent' : ''}`}>
                        {!isAdminEmbedded && (
                            <div className="px-5 pt-3 pb-2 flex justify-end">
                                {businessOptions.length > 1 && (
                                <SearchableBusinessSelect
                                    options={businessOptions}
                                    value={activeBusinessId || selectedBusinessId}
                                    onChange={(id) => {
                                        setSelectedBusinessId(id);
                                        setActiveBusinessId(id);
                                    }}
                                    widthClassName="w-80"
                                    showSubtitleWhenClosed={false}
                                />
                                )}
                            </div>
                        )}

                        <div
                            className={`flex flex-col ${isAdminEmbedded ? 'px-0' : 'px-5'} flex-1 min-h-0 transition-all duration-300 relative ${sheetWorkspaceFile ? 'overflow-hidden' : 'overflow-y-auto overflow-x-hidden'
                                }`}
                        >
                            {sheetWorkspaceFile ? (
                                <GoogleSheetWorkspacePanel
                                    file={sheetWorkspaceFile}
                                    onClose={() => setSheetWorkspaceFile(null)}
                                    onAfterAutoResync={onGoogleSheetWorkspaceAutoResync}
                                />
                            ) : (
                                <>
                                    {!isTutorialDemo && uploadNotice && (
                                        <div
                                            className={`mb-4 flex items-start gap-3 rounded-xl border px-4 py-3 ${uploadNotice.tone === 'error'
                                                ? 'border-red-200 bg-red-50 text-red-800'
                                                : uploadNotice.tone === 'processing'
                                                    ? 'border-blue-200 bg-blue-50 text-blue-800'
                                                    : 'border-green-200 bg-green-50 text-green-800'
                                                }`}
                                        >
                                            {uploadNotice.tone === 'processing' ? (
                                                <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin" />
                                            ) : uploadNotice.tone === 'error' ? (
                                                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                                            ) : (
                                                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm font-semibold">{uploadNotice.title}</div>
                                                {uploadNotice.detail && (
                                                    <div className="mt-0.5 text-sm opacity-90">{uploadNotice.detail}</div>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setUploadNotice(null)}
                                                className="rounded-md p-1 opacity-70 hover:bg-white/40 hover:opacity-100"
                                                aria-label="Dismiss upload notice"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                                    <div data-tutorial="documents-upload" className="shrink-0 min-h-[220px] md:min-h-[240px]">
                                        <FileUpload
                                            onUploadComplete={(payload) => {
                                                void handleUploadComplete(payload);
                                            }}
                                            businessId={effectiveBusinessId}
                                            availableTagLabels={availableTagLabels}
                                            onLabelsChanged={() => {
                                                refetchTagLabels();
                                                fetchFiles();
                                            }}
                                            externalFiles={droppedFiles}
                                            onExternalFilesHandled={() => setDroppedFiles([])}
                                        />
                                    </div>
                                    {/* Pending Review Section */}
                                    {showPendingReviewSection && (
                                        <div className="mt-3 w-full shrink-0 space-y-3">
                                            {pendingInvoiceDocs.length > 0 && (
                                                <div className="rounded-xl shadow-sm border border-amber-200/60 bg-gradient-to-br from-amber-50 via-orange-50/30 to-amber-100/40 min-w-0">
                                                    <div className="flex items-center gap-3 px-5 py-4 border-b border-amber-200/50 bg-white/50">
                                                        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-amber-500/15">
                                                            <AlertCircle className="w-5 h-5 text-amber-600" strokeWidth={2.5} />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h3 className="text-base font-semibold text-amber-900 tracking-tight">
                                                                Pending Invoices
                                                            </h3>
                                                            <p className="text-xs text-amber-700/80 mt-0.5">
                                                                {pendingInvoiceDocs.length} {pendingInvoiceDocs.length === 1 ? 'invoice' : 'invoices'} awaiting your review
                                                            </p>
                                                        </div>
                                                        <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2.5 rounded-lg bg-amber-500/20 text-amber-800 font-semibold text-sm tabular-nums">
                                                            {pendingInvoiceDocs.length}
                                                        </span>
                                                    </div>
                                                    <div className="p-4 space-y-2">
                                                        {pendingInvoiceDocs.map((invoice) => {
                                                            const invoiceId = getDocId(invoice);
                                                            if (!invoiceId) {
                                                                console.error('Invoice missing _id and id:', invoice);
                                                                return null;
                                                            }

                                                            return (
                                                                <div
                                                                    key={invoiceId}
                                                                    className="w-full bg-white/80 backdrop-blur-sm border border-amber-200/70 rounded-xl overflow-hidden flex items-stretch shadow-sm hover:shadow-md hover:border-amber-300/80 transition-all duration-200"
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={(e) => {
                                                                        if ((e.target as HTMLElement).closest('button')) {
                                                                            return;
                                                                        }
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleInvoiceClick(invoice);
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            e.preventDefault();
                                                                            handleInvoiceClick(invoice);
                                                                        }
                                                                    }}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            handleInvoiceClick(invoice);
                                                                        }}
                                                                        className="flex-1 text-left p-3 flex items-center gap-3 cursor-pointer hover:bg-amber-50 active:bg-amber-100 transition-colors"
                                                                    >
                                                                        <FileText className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="text-sm font-medium text-gray-900">
                                                                                {invoice.data.vendor?.name || 'Unknown Entity'}
                                                                            </p>
                                                                            <p className="text-xs text-gray-500">
                                                                                {invoice.data.invoice_number || 'No invoice number'} •
                                                                                ${invoice.data.total_amount?.toFixed(2) || '0.00'} •
                                                                                {invoice.data.transaction_date || 'No date'}
                                                                            </p>
                                                                        </div>
                                                                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={async (e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            if (window.confirm('Are you sure you want to delete this invoice?')) {
                                                                                try {
                                                                                    await documentsApi.deleteInvoice(invoiceId, effectiveBusinessId);
                                                                                    fetchAllInvoiceLikeDocs();
                                                                                    fetchFiles();
                                                                                } catch (err: any) {
                                                                                    alert(err.message || 'Failed to delete invoice');
                                                                                }
                                                                            }
                                                                        }}
                                                                        className="p-3 text-red-600 hover:text-red-700 hover:bg-red-50 active:bg-red-100 transition-colors border-l border-amber-200"
                                                                        title="Delete invoice"
                                                                    >
                                                                        <X className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {pendingExpenseDocs.length > 0 && (
                                                <div className="rounded-xl shadow-sm border border-cyan-200/70 bg-gradient-to-br from-cyan-50 via-sky-50/40 to-cyan-100/40 min-w-0">
                                                    <div className="flex items-center gap-3 px-5 py-4 border-b border-cyan-200/60 bg-white/50">
                                                        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-cyan-500/15">
                                                            <AlertCircle className="w-5 h-5 text-cyan-700" strokeWidth={2.5} />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h3 className="text-base font-semibold text-cyan-950 tracking-tight">
                                                                Pending Expenses
                                                            </h3>
                                                            <p className="text-xs text-cyan-800/80 mt-0.5">
                                                                {pendingExpenseDocs.length} {pendingExpenseDocs.length === 1 ? 'expense' : 'expenses'} awaiting your review
                                                            </p>
                                                        </div>
                                                        <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2.5 rounded-lg bg-cyan-500/15 text-cyan-900 font-semibold text-sm tabular-nums">
                                                            {pendingExpenseDocs.length}
                                                        </span>
                                                    </div>
                                                    <div className="p-4 space-y-2">
                                                        {pendingExpenseDocs.map((expense) => {
                                                            const expenseId = getDocId(expense);
                                                            if (!expenseId) {
                                                                console.error('Expense missing _id and id:', expense);
                                                                return null;
                                                            }

                                                            return (
                                                                <div
                                                                    key={expenseId}
                                                                    className="w-full bg-white/85 backdrop-blur-sm border border-cyan-200/80 rounded-xl overflow-hidden flex items-stretch shadow-sm hover:shadow-md hover:border-cyan-300/80 transition-all duration-200"
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={(e) => {
                                                                        if ((e.target as HTMLElement).closest('button')) return;
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleInvoiceClick(expense);
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            e.preventDefault();
                                                                            handleInvoiceClick(expense);
                                                                        }
                                                                    }}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            handleInvoiceClick(expense);
                                                                        }}
                                                                        className="flex-1 text-left p-3 flex items-center gap-3 cursor-pointer hover:bg-cyan-50 active:bg-cyan-100 transition-colors"
                                                                    >
                                                                        <FileText className="w-4 h-4 text-cyan-700 flex-shrink-0" />
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                                <p className="text-sm font-medium text-gray-900">
                                                                                    {getDocumentVendorName(expense, 'Unknown Vendor')}
                                                                                </p>
                                                                                {hasRelatedDocumentType(expense, 'duplicate_candidate') && (
                                                                                    <span className="inline-flex items-center rounded-md bg-cyan-100 px-2 text-[11px] font-medium text-cyan-800">
                                                                                        Duplicate
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <p className="text-xs text-gray-500">
                                                                                {expense.data.invoice_number || 'No expense number'} •
                                                                                ${expense.data.total_amount?.toFixed(2) || '0.00'} •
                                                                                {expense.data.transaction_date || 'No date'}
                                                                            </p>
                                                                        </div>
                                                                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={async (e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            if (window.confirm('Are you sure you want to delete this expense?')) {
                                                                                try {
                                                                                    await documentsApi.deleteExpense(expenseId, effectiveBusinessId);
                                                                                    fetchAllInvoiceLikeDocs();
                                                                                    fetchFiles();
                                                                                } catch (err: any) {
                                                                                    alert(err.message || 'Failed to delete expense');
                                                                                }
                                                                            }
                                                                        }}
                                                                        className="p-3 text-red-600 hover:text-red-700 hover:bg-red-50 active:bg-red-100 transition-colors border-l border-cyan-200"
                                                                        title="Delete expense"
                                                                    >
                                                                        <X className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Pending Bills Section */}
                                            {pendingBills.length > 0 && (
                                                <div className="rounded-xl shadow-sm border border-amber-200/60 bg-gradient-to-br from-amber-50 via-orange-50/30 to-amber-100/40 min-w-0">
                                                    <div className="flex items-center gap-3 px-5 py-4 border-b border-amber-200/50 bg-white/50">
                                                        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-amber-500/15">
                                                            <AlertCircle className="w-5 h-5 text-amber-600" strokeWidth={2.5} />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h3 className="text-base font-semibold text-amber-900 tracking-tight">
                                                                Pending Bills
                                                            </h3>
                                                            <p className="text-xs text-amber-700/80 mt-0.5">
                                                                {pendingBills.length} {pendingBills.length === 1 ? 'bill' : 'bills'} awaiting your review
                                                            </p>
                                                        </div>
                                                        <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2.5 rounded-lg bg-amber-500/20 text-amber-800 font-semibold text-sm tabular-nums">
                                                            {pendingBills.length}
                                                        </span>
                                                    </div>
                                                    <div className="p-4 space-y-2">
                                                        {pendingBills.map((bill) => {
                                                            const billId = getDocId(bill);
                                                            if (!billId) {
                                                                console.error('Bill missing _id and id:', bill);
                                                                return null;
                                                            }
                                                            return (
                                                                <div
                                                                    key={billId}
                                                                    className="w-full bg-white/80 backdrop-blur-sm border border-amber-200/70 rounded-xl overflow-hidden flex items-stretch shadow-sm hover:shadow-md hover:border-amber-300/80 transition-all duration-200"
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    onClick={(e) => {
                                                                        if ((e.target as HTMLElement).closest('button')) return;
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleBillClick(bill);
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                                            e.preventDefault();
                                                                            handleBillClick(bill);
                                                                        }
                                                                    }}
                                                                >
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            handleBillClick(bill);
                                                                        }}
                                                                        className="flex-1 text-left p-3 flex items-center gap-3 cursor-pointer hover:bg-amber-50 active:bg-amber-100 transition-colors"
                                                                    >
                                                                        <FileText className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                                <p className="text-sm font-medium text-gray-900">
                                                                                    {getDocumentVendorName(bill, 'Unknown Vendor')}
                                                                                </p>
                                                                                {hasRelatedDocumentType(bill, 'duplicate_candidate') && (
                                                                                    <span className="inline-flex items-center rounded-md bg-amber-100 px-2 text-[11px] font-medium text-amber-800">
                                                                                        Duplicate
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <p className="text-xs text-gray-500">
                                                                                {bill.data.invoice_number || 'No bill number'} •
                                                                                ${bill.data.total_amount?.toFixed(2) || '0.00'} •
                                                                                {bill.data.transaction_date || 'No date'}
                                                                            </p>
                                                                        </div>
                                                                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={async (e) => {
                                                                            e.preventDefault();
                                                                            e.stopPropagation();
                                                                            if (window.confirm('Are you sure you want to delete this bill?')) {
                                                                                try {
                                                                                    await documentsApi.deleteBill(billId, effectiveBusinessId);
                                                                                    fetchAllInvoiceLikeDocs();
                                                                                    fetchFiles();
                                                                                } catch (err: any) {
                                                                                    alert(err.message || 'Failed to delete bill');
                                                                                }
                                                                            }
                                                                        }}
                                                                        className="p-3 text-red-600 hover:text-red-700 hover:bg-red-50 active:bg-red-100 transition-colors border-l border-amber-200"
                                                                        title="Delete bill"
                                                                    >
                                                                        <X className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}


                                    {effectiveBusinessId && (
                                        <div className="mt-3 mb-2 flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                                            <div className="min-w-0 flex-1">
                                                <DriveBreadcrumb
                                                    location={driveLocation}
                                                    onNavigate={handleDriveNavigate}
                                                    onWorkspaceDrop={effectiveBusinessId ? handleWorkspaceDrop : undefined}
                                                />
                                            </div>
                                            <div className="flex w-full min-w-0 items-center gap-2 sm:w-auto sm:min-w-[420px] sm:justify-end">
                                                {MANUAL_EMPTY_FOLDERS_ENABLED && (driveLocation.kind === 'root' || driveLocation.kind === 'category' || driveLocation.kind === 'folder') && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setNewFolderOpen(true);
                                                            setNewFolderName('');
                                                            setNewFolderParentPathOverride(null);
                                                            if (driveLocation.kind === 'root') {
                                                                setNewFolderCategoryKey((prev) => prev || createFolderCategoryOptions[0]?.key || '');
                                                            } else {
                                                                setNewFolderCategoryKey(driveLocation.categoryKey);
                                                            }
                                                        }}
                                                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-indigo-50 hover:text-indigo-600"
                                                        aria-label="Create folder"
                                                        title="Create folder"
                                                    >
                                                        <FolderPlus className="h-4 w-4" />
                                                    </button>
                                                )}
                                                <label className="relative min-w-0 flex-1 sm:min-w-[300px] sm:max-w-[480px]">
                                                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                                    <input
                                                        type="search"
                                                        value={documentSearchInput}
                                                        onChange={(e) => setDocumentSearchInput(e.target.value)}
                                                        placeholder="Search folders, items, or vendors"
                                                        className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-700 outline-none transition-colors placeholder:text-gray-400 focus:border-gray-300"
                                                    />
                                                </label>

                                            </div>
                                        </div>
                                    )}

                                    {/* Document List — always render the shell, even before business scope resolves */}
                                    <div data-tutorial="documents-list" className={isMobile ? 'mt-1' : 'mt-1 flex-1 min-h-[360px]'}>
                                        <DocList
                                            handleShowEdit={handleShowEdit}
                                            isEmpty={displayIsEmpty && displayFolderRows.length === 0}
                                            files={displayFiles}
                                            folderRows={displayFolderRows}
                                            onRequestDelete={handleRequestDelete}
                                            onRequestMove={(file) => handleOpenOrganize([file])}
                                            onRequestMoveFolder={handleOpenFolderOrganize}
                                            onFileUpdated={fetchFiles}
                                            onInvoiceFileClick={handleInvoiceFileClick}
                                            onRequestQboSync={handleRequestQboSync}
                                            onBulkDelete={handleBulkDelete}
                                            onBulkConfirm={handleBulkConfirm}
                                            onBulkMove={handleOpenOrganize}
                                            businessId={effectiveBusinessId}
                                            availableTagLabels={availableTagLabels}
                                            onLabelsChanged={() => {
                                                refetchTagLabels();
                                                fetchFiles();
                                            }}
                                            currentPage={displayPage}
                                            totalPages={displayTotalPages}
                                            totalResults={displayTotalResults}
                                            pageSize={displayPageSize}
                                            onPrevPage={handlePrevPage}
                                            onNextPage={handleNextPage}
                                            onPageChange={setPage}
                                            onShowMetadata={(f) => setMetadataFile(f)}
                                            onGoogleSheetWorkspace={(f) => {
                                                setSheetWorkspaceFile(f);
                                                setMetadataFile(null);
                                            }}
                                            onWorkspaceDrop={effectiveBusinessId ? handleWorkspaceDrop : undefined}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {metadataFile && (
                <DocumentMetadataDrawer
                    file={metadataFile}
                    onClose={() => setMetadataFile(null)}
                    businessId={effectiveBusinessId}
                    allowReclassify={Boolean(effectiveBusinessId)}
                    onWorkspaceReclassified={() => {
                        void fetchFiles();
                        queryClient.invalidateQueries({ queryKey: ['workspace-tree', effectiveBusinessId] });
                        queryClient.invalidateQueries({ queryKey: ['workspace-summary', effectiveBusinessId] });
                    }}
                />
            )}

            <DocumentsTutorialMock
                stage={documentsTutorialStage}
                onClose={() => setDocumentsTutorialStage(null)}
            />

            {!isAdmin && showSetupGuidePanel && !isMobile ? (
                <div
                    aria-hidden="true"
                    className="relative h-full w-[30%] max-w-[300px] min-w-[350px] shrink-0 border bg-white shadow-sm"
                />
            ) : null}

            {/* Chat Panel */}
            {!isAdmin ? (
                <div className={showChat && !showSetupGuidePanel ? (isMobile ? 'fixed inset-0 z-[1999] bg-white w-full h-[100dvh] flex flex-col' : `flex bg-white chat-panel ${extendChat ? 'w-[40%] max-w-[600px]' : 'w-[30%] max-w-[300px]'}`) : 'hidden'}>
                    <div className="flex-1 overflow-auto flex flex-col">
                        <ChatPanel
                            page={'documents'}
                            activeGoogleSheet={
                                sheetWorkspaceFile
                                    ? {
                                        sourceDocumentId:
                                            sheetWorkspaceFile.google_sheet_source_document_id ||
                                            sheetWorkspaceFile.external_document?.source_document_id ||
                                            undefined,
                                        googleFileId:
                                            sheetWorkspaceFile.external_document?.google_file_id || undefined,
                                    }
                                    : undefined
                            }
                            onClose={() => setShowChat(false)}
                            sidebarOpen={showChat}
                            onDocumentsRefresh={() => {
                                void (async () => {
                                    await refreshWorkspaceFolders();
                                    await fetchFiles();
                                })();
                            }}
                            onGoogleSheetsImportQueued={() => {
                                const loc = buildDriveLocationFromPath(googleSheetsWorkspaceFolderPath(), 'other');
                                if (loc && loc.kind !== 'root') handleDriveNavigate(loc);
                            }}
                            documentsFolderLabel={documentsChatContextLabel}
                            initialPrompts={[
                                'Show me documents that need review',
                                'Show me recently uploaded files',
                                'What can you do?',
                            ]}
                        />
                    </div>
                    {!isMobile && (
                        <div onClick={() => setExtendChat(!extendChat)} className="border hover:bg-gray-200 hover:cursor-pointer py-5 rounded absolute bg-gray-100 top-20 -left-2">
                            {extendChat ? <ChevronRight size={18} className="text-gray-500" /> : <ChevronLeft size={18} className="text-gray-500" />}
                        </div>
                    )}
                </div>
            ) : null}

            {/* Chat Toggle Button */}
            {!showChat && !showSetupGuidePanel && !isAdmin ? <ChatButton handleShowChat={handleShowChat} /> : null}
        </div>
    );
}
